import { useEffect, useState, useRef, useCallback, memo } from "react";
import { api, formatError } from "../api";
import { useAuth } from "../AuthContext";
import AgentAvatar from "./AgentAvatar";
import LuiviaRobot from "./LuiviaRobot";
import PreviewIframe from "./PreviewIframe";
import PreviewPanel from "./PreviewPanel";

// Fase 1: único agente visible al cliente.
const STUDIO_AGENT_ID = "lluvia_studio";

// Fase 5: nombres amigables de tools — oculta detalles técnicos al cliente
const TOOL_LABELS = {
  write_workspace_file:    "Creando archivos del proyecto",
  read_workspace_file:     "Leyendo estructura del proyecto",
  list_workspace_files:    "Explorando archivos",
  search_replace_workspace:"Actualizando código",
  generate_component:      "Generando componente",
  generate_crud:           "Generando módulo de datos",
  generate_api_route:      "Creando endpoint de API",
  generate_landing_page:   "Diseñando landing page",
  generate_backend_module: "Generando módulo backend",
  generate_dashboard:      "Creando dashboard",
  generate_mobile_screen:  "Diseñando pantalla móvil",
  execute_build_plan:      "Construyendo app",
  generate_social_post:    "Generando contenido",
  generate_proposal:       "Generando propuesta",
  generate_pricing:        "Calculando precios",
  generate_report:         "Generando reporte",
  generate_pitch:          "Preparando pitch",
  shell_run:               "Configurando entorno",
  run_vps_command:         "Ejecutando en servidor",
  deploy_app_to_vps:       "Desplegando aplicación",
  restart_vps_service:     "Reiniciando servicios",
  docker_exec:             "Configurando contenedores",
  create_checkpoint:       "Guardando progreso",
  run_tests:               "Ejecutando pruebas",
  run_python:              "Procesando lógica",
  web_search:              "Investigando soluciones",
  web_browse:              "Consultando documentación",
  search_codebase:         "Analizando código",
  inspect_database:        "Revisando base de datos",
  get_openapi_schema:      "Consultando esquema de API",
  create_agent:            "Configurando especialista IA",
  update_agent:            "Actualizando especialista",
  provision_client_quick:  "Preparando entorno de trabajo",
  call_specialist_tool:    "Coordinando especialistas",
  push_to_my_github:       "Publicando en GitHub",
  github_list_repos:       "Consultando repositorios",
  get_platform_status:     "Verificando plataforma",
  service_health_check:    "Revisando servicios",
  self_diagnostic:         "Ejecutando diagnóstico",
  list_my_vps:             "Conectando con servidor",
  list_containers:         "Revisando infraestructura",
  memory_write:            "Guardando contexto",
  memory_search:           "Consultando memoria",
  task_planner:            "Planificando tareas",
  summarize_context:       "Resumiendo contexto",
  send_notification:       "Enviando notificación",
  send_quick_email:        "Enviando email",
  book_appointment:        "Procesando cita",
  crm_lookup:              "Consultando CRM",
  track_lead:              "Registrando contacto",
  analyze_architecture:    "Analizando arquitectura",
  auto_fix_build:          "Reparando errores de build",
  dependency_audit:        "Auditando dependencias",
  security_scan_basic:     "Verificando seguridad",
  init_project:            "Inicializando proyecto",
  plan_app:                "Diseñando arquitectura",
  get_build_context:       "Leyendo contexto del proyecto",
  update_build_phase:      "Actualizando fase",
  log_file_result:         "Registrando resultado",
  execute_build_plan:      "Construyendo app automáticamente",
  compile_check:              "Validando sintaxis",
  iterate_fix:               "Corrigiendo errores",
  configure_provider_token:  "Configurando proveedor cloud",
  provision_vps:             "Provisionando servidor",
  bootstrap_server:          "Instalando stack en servidor",
};

function BuildProgress({ toolCalls }) {
  if (!toolCalls?.length) return null;
  const steps = toolCalls
    .filter(tc => !["paypal_invoice_card","service_card","push_to_my_github",
                     "generate_haircut_preview","video_script_card",
                     "generate_promo_video"].includes(tc.name))
    .map(tc => ({ label: TOOL_LABELS[tc.name] || "Procesando", done: true }));
  if (!steps.length) return null;
  return (
    <div className="ls-v2-build-progress">
      <div className="ls-v2-build-hdr">
        <span className="ls-v2-build-hdr-dot"/>
        <span>Lluvia Studio trabajó en esto</span>
      </div>
      <div className="ls-v2-build-steps">
        {steps.map((s, i) => (
          <div key={i} className="ls-v2-build-step">
            <svg className="ls-v2-build-check" viewBox="0 0 16 16" width="13" height="13">
              <circle cx="8" cy="8" r="7" fill="rgba(16,185,129,0.15)" stroke="rgba(16,185,129,0.5)" strokeWidth="1"/>
              <path d="M5 8l2 2 4-4" stroke="#10b981" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Build SSE hook ─────────────────────────────────────────────────────────────
// Se conecta al stream de progreso del build y acumula eventos en tiempo real.
function useBuildSSE(projectId) {
  const [events, setEvents] = useState([]);
  const esRef = useRef(null);

  useEffect(() => {
    if (!esRef.current) setEvents([]);
  }, [projectId]);

  const connect = useCallback(() => {
    if (!projectId) return;
    if (esRef.current) { esRef.current.close(); esRef.current = null; }

    const token = localStorage.getItem("bot_admin_token") || localStorage.getItem("lluvia_token") || "";
    const url = `/api/console/projects/${projectId}/build/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        setEvents(prev => [...prev.slice(-80), ev]);
        if (ev.type === "done") { es.close(); esRef.current = null; }
      } catch {}
    };
    es.onerror = () => {};
  }, [projectId]);

  const disconnect = useCallback(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setEvents([]);
  }, []);

  return { events, connect, disconnect };
}

// ── Infra SSE hook ──────────────────────────────────────────────────────────────
// Progreso de bootstrap_server — conecta a /api/e2/servers/{id}/events
function useInfraSSE(serverId) {
  const [events, setEvents] = useState([]);
  const esRef = useRef(null);

  useEffect(() => {
    if (!esRef.current) setEvents([]);
  }, [serverId]);

  const connect = useCallback(() => {
    if (!serverId) return;
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    const token = localStorage.getItem("bot_admin_token") || localStorage.getItem("lluvia_token") || "";
    const url = `/api/e2/servers/${serverId}/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;
    es.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        setEvents(prev => [...prev.slice(-60), ev]);
        if (ev.type === "done") { es.close(); esRef.current = null; }
      } catch {}
    };
    es.onerror = () => {};
  }, [serverId]);

  const disconnect = useCallback(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setEvents([]);
  }, []);

  return { events, connect, disconnect };
}

const INFRA_STEPS = [
  "apt_update", "install_docker", "install_docker_compose",
  "install_nginx", "install_certbot", "configure_ufw",
  "create_deploy_user", "health_check", "register_vps",
];
const INFRA_STEP_LABELS = {
  apt_update:             "Actualizando sistema",
  install_docker:         "Instalando Docker",
  install_docker_compose: "Instalando Docker Compose",
  install_nginx:          "Instalando Nginx",
  install_certbot:        "Instalando SSL (Certbot)",
  configure_ufw:          "Configurando firewall",
  create_deploy_user:     "Creando usuario deploy",
  health_check:           "Verificando servicios",
  register_vps:           "Registrando servidor",
};

function InfraProgress({ liveEvents = [] }) {
  if (!liveEvents.length) return null;

  const doneSet   = new Set(liveEvents.filter(e => e.type === "step_ok").map(e => e.step));
  const failedSet = new Set(liveEvents.filter(e => e.type === "step_err").map(e => e.step));
  const activeStep = [...liveEvents].reverse().find(e => e.type === "step_start")?.step;
  const doneEv    = liveEvents.find(e => e.type === "done");
  const isDone    = !!doneEv;
  const allOk     = doneEv?.ok ?? false;
  const completed = doneSet.size + failedSet.size;
  const pct       = Math.round((completed / INFRA_STEPS.length) * 100);
  const isDry     = liveEvents.some(e => e.dry_run);

  const panelMod = isDone ? (allOk ? "done" : "error") : "running";

  return (
    <div className={`bap-panel bap-panel--${panelMod === "running" ? "generating" : panelMod}`}>
      <div className="bap-header">
        <span className="bap-header-dot" />
        <span className="bap-header-label">
          {isDone
            ? (allOk ? `Servidor listo${isDry ? " (simulación)" : ""}` : "Bootstrap con errores")
            : `Instalando stack… ${pct}%${isDry ? " · simulación" : ""}`}
        </span>
      </div>
      <div className="bap-log-list">
        {INFRA_STEPS.map(step => {
          const ok     = doneSet.has(step);
          const fail   = failedSet.has(step);
          const active = !ok && !fail && step === activeStep;
          return (
            <div key={step}
                 className={`bap-log-entry ${ok ? "bap-log-ok" : fail ? "bap-log-fail" : active ? "bap-log-running" : "bap-log-pending"}`}>
              <span className={`bap-icon ${ok ? "bap-icon--ok" : fail ? "bap-icon--fail" : ""}`}>
                {ok ? "✓" : fail ? "✗" : active ? "⟳" : "·"}
              </span>
              <span>{INFRA_STEP_LABELS[step]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BuildAutoProgress({ project, liveEvents = [], onPreview }) {
  const phase = project?.phase;
  // [B6-4] Frozen projects show a banner even in early phases
  const isFrozen = !!project?.frozen;
  if (!isFrozen && (!phase || phase === "idea" || phase === "planning" || phase === "scaffolding")) return null;
  if (!phase) return null;

  const filePlan   = project?.plan?.file_plan || [];
  const created    = project?.files_created   || [];
  const failed     = project?.files_failed    || [];
  const buildLog   = project?.build_log       || [];
  const total      = filePlan.length;
  const done       = created.length + failed.length;
  const pct        = total > 0 ? Math.round((done / total) * 100) : 0;
  const previewUrl = project?.preview_url;

  const isGenerating = phase === "generating";
  const isFixing     = phase === "fixing";
  const isValidating = phase === "validating";
  const isDone       = phase === "preview" || phase === "done";
  const isError      = phase === "error";

  // Live SSE events take priority over polled build_log for real-time display
  const hasLive = liveEvents.length > 0 && (isGenerating || isFixing || isValidating);

  // Current file: from live SSE first, then fall back to polled build_log
  const currentFile = (() => {
    if (hasLive) {
      const last = [...liveEvents].reverse().find(e => e.type === "file_start");
      const resolved = liveEvents.find(e => e.file === last?.file && (e.type === "file_ok" || e.type === "file_fail" || e.type === "file_fixed"));
      return (last && !resolved) ? last.file : null;
    }
    const lastGen = [...buildLog].reverse().find(e => e.action === "file_generating");
    if (!lastGen) return null;
    const resolved = buildLog.find(
      e => e.file === lastGen.file &&
           (e.action === "file_generated" || e.action === "file_failed" || e.action === "file_auto_fixed")
    );
    return resolved ? null : lastGen.file;
  })();

  // Live count: union of MongoDB files_created/failed + SSE events by file path.
  // This prevents both regression (going backwards after refresh) and double-counting
  // (when polling catches up with SSE). Deduplication is by file path.
  const liveOkSet   = new Set(hasLive
    ? [...created, ...liveEvents.filter(e => e.type === "file_ok" || e.type === "file_fixed").map(e => e.file).filter(Boolean)]
    : created);
  const liveFailSet = new Set(hasLive
    ? [...failed, ...liveEvents.filter(e => e.type === "file_fail").map(e => e.file).filter(Boolean)]
    : failed);
  const liveDone   = liveOkSet.size;
  const liveFailed = liveFailSet.size;
  const livePct    = total > 0 ? Math.round(((liveDone + liveFailed) / total) * 100) : pct;

  // Recent log: from SSE when live, else from polled build_log
  const recentLog = hasLive
    ? liveEvents
        .filter(e => ["file_ok", "file_fixed", "file_fail", "preview_ready"].includes(e.type))
        .slice(-6)
        .map(e => ({
          file: e.file || "",
          ok: e.type !== "file_fail",
          action: e.type === "file_fixed" ? "file_auto_fixed" : e.type === "preview_ready" ? "preview_started" : "file_generated",
          detail: e.error || "",
        }))
    : (() => {
        const SKIP_ACTIONS = new Set(["build_started", "file_generating"]);
        return buildLog.filter(e => !SKIP_ACTIONS.has(e.action)).slice(-6);
      })();

  const actionIcon = (entry) => {
    if (!entry.ok)             return <span className="bap-icon bap-icon--fail">✗</span>;
    if (entry.action === "file_auto_fixed") return <span className="bap-icon bap-icon--fix">↻</span>;
    if (entry.action === "preview_started") return <span className="bap-icon bap-icon--ok">🚀</span>;
    return <span className="bap-icon bap-icon--ok">✓</span>;
  };

  return (
    <div className={`bap-panel bap-panel--${phase}${isFrozen ? " bap-panel--frozen" : ""}`}>
      {/* [B6-4] Frozen banner */}
      {isFrozen && (
        <div className="bap-frozen-banner">
          🔒 {project.freeze_label || "Proyecto congelado"} · reconstrucciones bloqueadas
        </div>
      )}
      <div className="bap-header">
        <span className="bap-header-dot" />
        <span className="bap-header-label">
          {isGenerating && `Construyendo… ${liveDone}/${total} archivos`}
          {isFixing     && "Corrigiendo errores automáticamente"}
          {isValidating && "Validando archivos generados"}
          {isDone       && `✓ Build completado — ${created.length} archivos`}
          {isError      && "Error en la construcción"}
          {!isGenerating && !isFixing && !isValidating && !isDone && !isError && isFrozen && "Versión congelada"}
        </span>
        <div className="bap-phases">
          <span className={`bap-phase${(isGenerating || isFixing || isValidating || isDone) ? " active" : ""}`}>Building</span>
          <span className="bap-phase-sep">›</span>
          <span className={`bap-phase${(isValidating || isDone) ? " active" : ""}`}>Testing</span>
          <span className="bap-phase-sep">›</span>
          <span className={`bap-phase${isDone ? " active bap-phase--live" : ""}`}>Live</span>
        </div>
        {/* B6-2: Strategy badge */}
        {project?.strategy && (
          <span className={`bap-strategy-badge bap-strategy-badge--${project.strategy}`}>
            {project.strategy === "template" ? "📦 Template" :
             project.strategy === "hybrid"   ? "⚡ Hybrid"   : "✨ IA"}
          </span>
        )}
        {total > 0 && (
          <span className="bap-counter">{livePct}%</span>
        )}
      </div>

      {total > 0 && (
        <div className="bap-progress-track">
          <div
            className={`bap-progress-bar${isDone ? " done" : isError ? " error" : ""}`}
            style={{ width: `${isDone ? 100 : livePct}%` }}
          />
        </div>
      )}

      {recentLog.length > 0 && (
        <div className="bap-log">
          {recentLog.map((entry, i) => (
            <div key={i} className={`bap-log-entry${!entry.ok ? " fail" : ""}`}>
              {actionIcon(entry)}
              <span className="bap-log-text">
                {entry.file ? (
                  <><code className="bap-filename">{entry.file}</code>
                  {entry.detail && entry.action === "file_auto_fixed" && (
                    <span className="bap-log-detail"> — auto-corregido</span>
                  )}</>
                ) : (
                  entry.detail?.slice(0, 70)
                )}
              </span>
            </div>
          ))}
          {(isGenerating || isFixing) && (
            <div className="bap-log-entry bap-log-entry--working">
              <span className="bap-spinner" />
              <span className="bap-log-text">
                {currentFile
                  ? <>{isFixing ? "Corrigiendo" : "Generando"} <code className="bap-filename">{currentFile}</code>…</>
                  : isFixing ? "Corrigiendo errores…" : "Generando siguiente archivo…"
                }
              </span>
            </div>
          )}
        </div>
      )}

      {isDone && (
        <div className="bap-actions">
          {previewUrl && (
            <button className="bap-btn bap-btn--preview" onClick={() => onPreview && onPreview(project?.app_slug)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/>
              </svg>
              Ver preview
            </button>
          )}
          {failed.length > 0 && (
            <span className="bap-warn">{failed.length} archivos con errores</span>
          )}
        </div>
      )}
    </div>
  );
}

const LS_ACTIVE_SESSION = "lluvia_active_session";

const PHASE_LABELS = {
  idea:        "💡 Idea",
  planning:    "📐 Planificando",
  scaffolding: "🏗 Estructura",
  generating:  "⚡ Generando",
  validating:  "🔍 Validando",
  fixing:      "🔧 Corrigiendo",
  preview:     "👁 Preview live",
  done:        "✅ Completado",
  error:       "❌ Error",
};

export default function BossConsole() {
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const [agents, setAgents] = useState([]);
  const [sessions, setSessions] = useState([]);
  // Restore last active session from localStorage so a page refresh never loses context.
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem(LS_ACTIVE_SESSION) || null; }
    catch (_) { return null; }
  });
  const [activeSession, setActiveSession] = useState(null);
  const [balance, setBalance] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [err, setErr] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [packs, setPacks] = useState({});
  const [packsConfigured, setPacksConfigured] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{url, name, preview, uploading}]
  const [dragOver, setDragOver] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraErr, setCameraErr] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [appPickerForPush, setAppPickerForPush] = useState(null); // null | [{name,size_bytes,modified}]
  const [showGitHubSetup, setShowGitHubSetup] = useState(false);
  const [githubConfigured, setGithubConfigured] = useState(false);
  // P3: persist preview state across reloads
  const [previewOpen, setPreviewOpen] = useState(
    () => localStorage.getItem("pv_open") === "1"
  );
  const [previewEverOpened, setPreviewEverOpened] = useState(
    () => !!localStorage.getItem("pv_slug")
  );
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [previewSlug, setPreviewSlug] = useState(
    () => localStorage.getItem("pv_slug") || null
  );
  const [mobileTabOpen, setMobileTabOpen] = useState(false); // mobile: preview tab active
  const [userApps, setUserApps] = useState([]); // list of user's apps
  const [userProjects, setUserProjects] = useState([]); // list of App Builder projects
  const [activeProject, setActiveProject] = useState(null); // project context for active session
  const buildPollRef = useRef(null);       // polling interval for build progress
  const postBuildCountRef = useRef(0);    // settling polls after build completes
  const { events: buildLiveEvents, connect: connectBuildSSE, disconnect: disconnectBuildSSE } = useBuildSSE(activeProject?.id);
  const [buildAutoStart, setBuildAutoStart] = useState(false); // autoStart PreviewPanel when build started it
  const [activeBootstrapServerId, setActiveBootstrapServerId] = useState(null);
  const { events: infraLiveEvents, connect: connectInfraSSE, disconnect: disconnectInfraSSE } = useInfraSSE(activeBootstrapServerId);
  // Build bubbles: synthetic agent messages injected from SSE milestone events
  const [buildBubbles, setBuildBubbles] = useState([]);
  const buildBubbleKeysRef = useRef(new Set());
  // New project modal
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDesc, setNewProjectDesc] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const nativeCameraInputRef = useRef(null);  // <input capture> fallback iOS/Android
  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  // Auto-resize del textarea estilo ChatGPT
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 200) + "px";
  }, [input]);

  const refreshAll = async () => {
    try {
      const [a, s, c, p] = await Promise.all([
        api.get("/console/agents"),
        api.get("/console/sessions"),
        api.get("/console/credits/me"),
        api.get("/paypal/packs"),
      ]);
      setAgents(a.data.agents);
      const loadedSessions = s.data.sessions;
      setSessions(loadedSessions);
      setBalance(c.data.balance);
      setPacks(p.data.packs || {});
      setPacksConfigured(p.data.configured);
      // Validate the restored activeId — if it no longer exists in the list, clear it
      setActiveId(prev => {
        if (!prev) return prev;
        const stillExists = loadedSessions.some(sess => sess.id === prev);
        if (!stillExists) {
          try { localStorage.removeItem(LS_ACTIVE_SESSION); } catch (_) {}
          return null;
        }
        return prev;
      });
    } catch (e) {
      setErr(formatError(e));
    }
    // Load user apps for preview
    try {
      const { data } = await api.get("/me/apps");
      setUserApps(data.apps || []);
    } catch (_) {}
    // Load App Builder projects
    try {
      const { data } = await api.get("/console/projects");
      setUserProjects(data.projects || []);
    } catch (_) {}
    // Load github config status
    try {
      const { data } = await api.get("/me/settings");
      setGithubConfigured(!!(data.has_github_token && data.github_repo));
    } catch (_) {}
  };

  // Create a new App Builder project with its own session atomically
  const createProject = async () => {
    if (!newProjectName.trim() || creatingProject) return;
    setCreatingProject(true);
    try {
      const { data } = await api.post("/console/projects", {
        name: newProjectName.trim(),
        description: newProjectDesc.trim(),
        agent_id: STUDIO_AGENT_ID,
        auto_create_session: true,
      });
      await refreshAll();
      // Activate the new project's session
      if (data.session_id) setActiveId(data.session_id);
      setShowNewProjectModal(false);
      setNewProjectName("");
      setNewProjectDesc("");
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setCreatingProject(false);
    }
  };

  // Select a project: activate its session (or create one if missing)
  const selectProject = async (project) => {
    if (project.session_id) {
      // Project already has a session — just switch to it
      setActiveId(project.session_id);
      return;
    }
    // No session yet — create one and link it to the existing project
    try {
      const r = await api.post("/console/sessions", {
        agent_id: STUDIO_AGENT_ID,
        title: project.name,
      });
      const newSessionId = r.data.id;
      // Link via PUT /projects/{id} with session_id — updates both project + session docs
      await api.put(`/console/projects/${project.id}`, { session_id: newSessionId });
      await refreshAll();
      setActiveId(newSessionId);
    } catch (e) {
      setErr(formatError(e));
    }
  };

  const deleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este proyecto? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/console/projects/${projectId}`);
      setUserProjects(prev => prev.filter(p => p.id !== projectId));
      // If this was the active project, clear it
      setActiveProject(prev => prev?.id === projectId ? null : prev);
    } catch (e2) {
      setErr(formatError(e2));
    }
  };

  // Persist activeId to localStorage on every change so refresh restores context
  useEffect(() => {
    try {
      if (activeId) localStorage.setItem(LS_ACTIVE_SESSION, activeId);
      else localStorage.removeItem(LS_ACTIVE_SESSION);
    } catch (_) {}
  }, [activeId]);

  // Detecta el slug de la app activa para abrir el preview.
  // Fuente primaria: activeProject (App Builder moderno).
  // Fallback: única app del usuario.
  const detectPreviewSlug = (messages) => {
    if (activeProject?.app_slug) return activeProject.app_slug;
    if (userApps.length === 1) return userApps[0].name;
    return null;
  };

  useEffect(() => { refreshAll(); }, []);

  // Si el visitante vino del demo publico, hay un seed en localStorage con
  // {app_name, brand_color}. Auto-creamos una sesion con app_builder_pro
  // y mandamos el mensaje para que ensamble la app inmediatamente.
  useEffect(() => {
    if (!agents.length) return;
    let seed = null;
    try { seed = JSON.parse(localStorage.getItem("lluvia_demo_seed") || "null"); } catch (_) {}
    if (!seed || !seed.app_name) return;
    localStorage.removeItem("lluvia_demo_seed"); // consumir una sola vez
    (async () => {
      try {
        const r = await api.post("/console/sessions", { agent_id: "app_builder_pro" });
        await refreshAll();
        setActiveId(r.data.id);
        // dar tiempo a que activeSession se cargue
        setTimeout(() => {
          const text = `Llamala "${seed.app_name}" y usá color ${seed.brand_color || "#5B8DEF"}. Generala ya.`;
          window.dispatchEvent(new CustomEvent("lluvia:compose-message", { detail: { text, send: true } }));
        }, 800);
      } catch (e) { setErr(formatError(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents.length]);

  useEffect(() => {
    if (!activeId) { setActiveSession(null); setActiveProject(null); return; }
    api.get(`/console/sessions/${activeId}`)
      .then((r) => {
        setActiveSession(r.data);
        // If session has a project, load its context for the header phase badge
        if (r.data?.project_id) {
          api.get(`/console/projects/${r.data.project_id}`)
            .then((p) => setActiveProject(p.data))
            .catch(() => setActiveProject(null));
        } else {
          setActiveProject(null);
        }
      })
      .catch((e) => {
        // Session deleted or not found — clear it so the UI doesn't get stuck
        if (e?.response?.status === 404) {
          setActiveId(null);
          return;
        }
        setErr(formatError(e));
      });
  }, [activeId]);

  // Build SSE — conectar cuando entra en fase activa, desconectar al terminar
  useEffect(() => {
    // "scaffolding" included so SSE connects while plan_app is running (avoids race)
    const ACTIVE_PHASES = new Set(["scaffolding", "generating", "fixing", "validating"]);
    if (activeProject?.id && ACTIVE_PHASES.has(activeProject?.phase)) {
      connectBuildSSE();
    } else {
      disconnectBuildSSE();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id, activeProject?.phase]);

  // Infra SSE — conectar cuando bootstrap_server fue llamado
  useEffect(() => {
    if (activeBootstrapServerId) {
      connectInfraSSE();
    } else {
      disconnectInfraSSE();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBootstrapServerId]);

  // P3: persist previewOpen + previewSlug to localStorage
  useEffect(() => {
    localStorage.setItem("pv_open", previewOpen ? "1" : "0");
  }, [previewOpen]);

  useEffect(() => {
    if (previewSlug) localStorage.setItem("pv_slug", previewSlug);
    else localStorage.removeItem("pv_slug");
  }, [previewSlug]);

  // P3: auto-reconnect PreviewPanel on tab visibility regain
  // (handled inside PreviewPanel via autoStart on mount — this guards the slug)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && previewSlug && previewOpen) {
        // previewSlug unchanged → PreviewPanel will re-POST /preview on its own
        // (autoStart fires on each appSlug change; this just ensures slug is set)
        setPreviewSlug(s => s);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [previewSlug, previewOpen]);

  // Build bubbles — convert SSE milestone events into proactive agent chat bubbles
  useEffect(() => {
    if (!buildLiveEvents.length) return;
    const total = buildLiveEvents.find(e => e.type === "start")?.total || 0;

    for (const ev of buildLiveEvents) {
      let key = null;
      let label = null;

      if (ev.type === "milestone" && ev.key) {
        key   = ev.key;
        label = ev.label;
      } else if (ev.type === "file_ok" && ev.done === 1) {
        key   = "first_file";
        label = `📝 Primer archivo generado: \`${ev.file}\``;
      } else if (ev.type === "done") {
        key   = ev.ok ? "done_ok" : "done_fail";
        label = ev.ok
          ? `✅ Build completo — ${ev.files_ok} archivos en ${ev.elapsed}s. ¡Tu app está lista!`
          : `⚠️ Build con ${ev.files_failed} error(es). Revisando y corrigiendo...`;
      }

      if (key && label && !buildBubbleKeysRef.current.has(key)) {
        buildBubbleKeysRef.current.add(key);
        setBuildBubbles(prev => [...prev, {
          id:           `build-bubble-${key}-${Date.now()}`,
          role:         "assistant",
          content:      label,
          isBuildBubble: true,
          ts:           new Date().toISOString(),
          pct:          ev.pct || null,
        }]);
        // Auto-open preview when the app is live
        if (ev.type === "preview_ready" || key === "preview_ready" || key === "done_ok") {
          const slug = ev.url
            ? ev.url.split("/apps/")[1]?.split("/")[0]
            : (activeProject?.app_slug || null);
          if (slug) {
            setPreviewSlug(slug);
            setPreviewEverOpened(true);
            setPreviewOpen(true);
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildLiveEvents]);

  // Reset infra + build bubbles al cambiar de sesión
  useEffect(() => {
    setActiveBootstrapServerId(null);
    setBuildBubbles([]);
    buildBubbleKeysRef.current = new Set();
  }, [activeId]);

  // Build progress polling — runs while build is in-progress or post-build settling
  useEffect(() => {
    // "scaffolding" included to survive the race where execute_build_plan background
    // task hasn't yet written "generating" to DB when the post-send refresh fires.
    const ACTIVE_PHASES = new Set(["scaffolding", "generating", "fixing", "validating"]);
    const pid   = activeProject?.id;
    const phase = activeProject?.phase;

    // Reset settle counter whenever we enter an active build phase
    if (ACTIVE_PHASES.has(phase)) postBuildCountRef.current = 0;

    // Poll while active, OR while "done" with no preview_url yet (up to 10×2s = 20s)
    const shouldPoll = pid && (
      ACTIVE_PHASES.has(phase) ||
      (phase === "done" && !activeProject?.preview_url && postBuildCountRef.current < 10)
    );

    if (!shouldPoll) {
      if (buildPollRef.current) { clearInterval(buildPollRef.current); buildPollRef.current = null; }
      return;
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/console/projects/${pid}`);
        setActiveProject(data);
        if (!ACTIVE_PHASES.has(data.phase)) {
          // Build done — keep polling briefly if preview_url not yet available
          if (data.phase === "done" && !data.preview_url) {
            postBuildCountRef.current += 1;
            if (postBuildCountRef.current < 10) return; // keep interval alive
          }
          clearInterval(buildPollRef.current);
          buildPollRef.current = null;
          api.get("/console/projects").then(r => setUserProjects(r.data.projects || [])).catch(() => {});
        }
      } catch (_) {}
    };

    buildPollRef.current = setInterval(poll, 2000);
    return () => { if (buildPollRef.current) { clearInterval(buildPollRef.current); buildPollRef.current = null; } };
  }, [activeProject?.id, activeProject?.phase, activeProject?.preview_url]);

  // Auto-open preview when build_engine starts it early (Emergent-style)
  // Triggered when activeProject.preview_url transitions from null → value.
  // NOTE: setPreviewEverOpened and setPreviewSlug are ALWAYS called when preview_url
  // exists — NOT gated on previewOpen. The old gate created a stale-closure bug:
  // when switching between multiple projects that both have preview_url, the second
  // project's auto-open saw previewOpen=true (stale) and skipped setPreviewEverOpened,
  // leaving the bc-preview-pane div unmounted (never rendered).
  useEffect(() => {
    const url  = activeProject?.preview_url;
    const slug = activeProject?.app_slug;
    if (!url || !slug) return;
    setPreviewSlug(slug);
    setPreviewEverOpened(true);
    setBuildAutoStart(true);
    setPreviewOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.preview_url, activeProject?.app_slug]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [activeSession?.messages?.length, sending, activeProject?.phase]);

  const createSession = async (agentId) => {
    setShowPicker(false);
    try {
      const r = await api.post("/console/sessions", { agent_id: agentId });
      await refreshAll();
      setActiveId(r.data.id);
    } catch (e) { setErr(formatError(e)); }
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    const readyImages = attachments.filter((a) => a.url && !a.uploading);
    if ((!text && readyImages.length === 0) || sending) return;

    setInput("");
    setSending(true);
    setErr("");
    const imageUrls = readyImages.map((a) => a.url);
    setAttachments([]);

    // Auto-create E1 session if none exists (Fase 3: primer mensaje crea sesión)
    let targetId = activeId;
    if (!targetId) {
      try {
        const r = await api.post("/console/sessions", { agent_id: STUDIO_AGENT_ID });
        await refreshAll();
        targetId = r.data.id;
        setActiveId(r.data.id);
      } catch (e) {
        setErr(formatError(e));
        setSending(false);
        return;
      }
    } else {
      // Optimistic UI solo cuando ya hay sesión
      setActiveSession((p) => p ? {
        ...p,
        messages: [...(p.messages || []), {
          id: "tmp" + Date.now(),
          role: "user",
          content: text,
          image_urls: imageUrls,
          ts: new Date().toISOString(),
        }],
      } : p);
    }

    try {
      const r = await api.post(`/console/sessions/${targetId}/messages`, {
        text: text || "(imagen)",
        image_urls: imageUrls.length ? imageUrls : undefined,
      });
      setBalance(r.data.balance);
      const fresh = await api.get(`/console/sessions/${targetId}`);
      setActiveSession(fresh.data);
      const s = await api.get("/console/sessions");
      setSessions(s.data.sessions);
      // Refresh activeProject so the build polling picks up phase="generating"
      // (execute_build_plan runs as background task — DB is updated during LLM call)
      if (fresh.data?.project_id) {
        api.get(`/console/projects/${fresh.data.project_id}`)
          .then(p => setActiveProject(p.data))
          .catch(() => {});
      }
      // Detect bootstrap_server call → connect infra SSE
      const msgs = fresh.data?.messages || [];
      for (let i = msgs.length - 1; i >= 0; i--) {
        const tc = (msgs[i].tool_calls || []).find(t => t.name === "bootstrap_server");
        if (tc?.args?.server_id) { setActiveBootstrapServerId(tc.args.server_id); break; }
      }
    } catch (e) {
      setErr(formatError(e));
    } finally { setSending(false); }
  };

  // Listener global para que las rich cards puedan pre-rellenar y enviar
  // mensajes (ej: CTA "Generar video real con Sora 2" desde VideoScriptCard).
  useEffect(() => {
    const onCompose = (e) => {
      const { text, send: shouldSend } = e.detail || {};
      if (!text) return;
      if (shouldSend) {
        send(text);  // enviar directamente
      } else {
        setInput((prev) => (prev ? prev + " " + text : text));
        textareaRef.current?.focus();
      }
    };
    window.addEventListener("lluvia:compose-message", onCompose);
    return () => window.removeEventListener("lluvia:compose-message", onCompose);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, attachments]);

  // ---- IMAGENES: subir archivo y adjuntar al proximo mensaje
  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Solo se permiten imagenes (JPG, PNG, GIF, WebP)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErr("Imagen demasiado grande (max 8MB)");
      return;
    }
    // Auto-crear sesión E1 si no existe (necesaria para upload endpoint)
    let uploadSessionId = activeId;
    if (!uploadSessionId) {
      try {
        const r = await api.post("/console/sessions", { agent_id: STUDIO_AGENT_ID });
        await refreshAll();
        uploadSessionId = r.data.id;
        setActiveId(r.data.id);
      } catch (e) { setErr(formatError(e)); return; }
    }
    const previewUrl = URL.createObjectURL(file);
    const tmpId = "att" + Date.now() + Math.random();
    setAttachments((prev) => [...prev, { id: tmpId, preview: previewUrl, name: file.name, uploading: true }]);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/console/sessions/${uploadSessionId}/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => prev.map((a) =>
        a.id === tmpId ? { ...a, url: r.data.url, uploading: false } : a
      ));
    } catch (e) {
      setAttachments((prev) => prev.filter((a) => a.id !== tmpId));
      setErr(formatError(e));
    }
  };

  const onFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    files.slice(0, 4).forEach(uploadImage);
    if (e.target) e.target.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
    files.slice(0, 4).forEach(uploadImage);
  };

  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imgs = items.filter((i) => i.type.startsWith("image/")).map((i) => i.getAsFile()).filter(Boolean);
    if (imgs.length) {
      e.preventDefault();
      imgs.slice(0, 4).forEach(uploadImage);
    }
  };

  // ---- CAMARA: abrir modal con getUserMedia y capturar foto a canvas
  // En entornos donde getUserMedia esta bloqueado (iframe Preview iOS,
  // WebView, contexto inseguro), automaticamente caemos al <input capture>
  // nativo que iOS Safari SI permite incluso dentro del Preview.
  const triggerNativeCamera = () => {
    closeCamera();
    if (nativeCameraInputRef.current) {
      nativeCameraInputRef.current.value = "";
      nativeCameraInputRef.current.click();
    }
  };

  const openCamera = async () => {
    setCameraErr("");
    // Si no hay API de mediaDevices (Safari iframe / contexto inseguro),
    // saltamos directo a la camara nativa nativa del SO.
    if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
      triggerNativeCamera();
      return;
    }
    setCameraOpen(true);
    // Esperar al siguiente tick para que el <video> exista
    await new Promise((r) => setTimeout(r, 60));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        try { await cameraVideoRef.current.play(); } catch (_) {}
      }
    } catch (e) {
      const name = e?.name || "";
      const isBlocked = name === "NotAllowedError" || /not allowed/i.test(e?.message || "");
      if (isBlocked) {
        // Preview de Emergent / iframe en iOS bloquea getUserMedia.
        // Caemos al input capture nativo (que SI funciona).
        triggerNativeCamera();
        return;
      }
      const msg = name === "NotFoundError"
        ? "No detecté ninguna cámara. Probá adjuntar desde la galería."
        : `No pude abrir la cámara: ${e?.message || e}. Tocá '📱 Usar cámara del teléfono' para usar la cámara nativa.`;
      setCameraErr(msg);
    }
  };

  const closeCamera = () => {
    const stream = cameraStreamRef.current;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) cameraVideoRef.current.srcObject = null;
    setCameraOpen(false);
  };

  const flipCamera = async () => {
    // Alterna entre front/rear apagando y volviendo a abrir con facingMode contrario.
    const current = cameraStreamRef.current?.getVideoTracks?.()[0]?.getSettings?.()?.facingMode;
    const newFacing = current === "user" ? "environment" : "user";
    // Cortar tracks viejos antes de pedir otros (algunos WebViews lo requieren).
    const oldStream = cameraStreamRef.current;
    if (oldStream) oldStream.getTracks().forEach((t) => t.stop());
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) cameraVideoRef.current.srcObject = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: newFacing }, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        try { await cameraVideoRef.current.play(); } catch (_) {}
      }
    } catch (e) {
      // En iOS Preview iframe el flip suele fallar con NotAllowed; caemos al nativo.
      triggerNativeCamera();
    }
  };

  const capturePhoto = async () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth) {
      setCameraErr("La cámara aún no tiene video. Esperá un segundo y reintentá.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
    if (!blob) {
      setCameraErr("No pude capturar la foto. Reintentá.");
      return;
    }
    const file = new File([blob], `camara_${Date.now()}.jpg`, { type: "image/jpeg" });
    closeCamera();
    uploadImage(file);
  };

  // Cerrar el menu de adjuntos al clickear fuera
  useEffect(() => {
    if (!showAttachMenu) return;
    const onClick = (e) => {
      if (!e.target.closest(".bc-attach-menu") && !e.target.closest(".bc-attach-btn")) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [showAttachMenu]);

  // Limpiar el stream al desmontar (evita el clasico "pantalla negra" en reintentos)
  useEffect(() => {
    return () => {
      const s = cameraStreamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const delSession = async (id) => {
    if (!window.confirm("Borrar este hilo?")) return;
    await api.delete(`/console/sessions/${id}`);
    if (activeId === id) setActiveId(null);
    refreshAll();
  };

  // ---- VOZ: grabar y transcribir
  const toggleRecord = async () => {
    if (recording) {
      mediaRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("audio", blob, "voice.webm");
        try {
          const r = await api.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setBalance(r.data.balance);
          if (r.data.text) send(r.data.text);
        } catch (e) { setErr(formatError(e)); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      setErr("Permiso de microfono denegado");
    }
  };

  const playTts = async (text, voice) => {
    try {
      const resp = await api.post("/voice/tts", { text, voice: voice || "alloy" },
        { responseType: "blob" });
      const balance = resp.headers["x-balance-after"];
      if (balance) setBalance(parseInt(balance, 10));
      const url = URL.createObjectURL(resp.data);
      new Audio(url).play();
    } catch (e) { setErr(formatError(e)); }
  };

  // ---- PAYPAL
  const buyPack = async (packId) => {
    try {
      const r = await api.post("/paypal/create-order", { pack: packId });
      const w = window.open(r.data.approve_url, "_blank", "width=500,height=700");
      // Polling: cuando el usuario apruebe y vuelva, capturamos
      const orderId = r.data.order_id;
      const poll = setInterval(async () => {
        if (w && w.closed) {
          clearInterval(poll);
          try {
            const cap = await api.post(`/paypal/capture/${orderId}`);
            if (cap.data.balance) setBalance(cap.data.balance);
            setShowShop(false);
            alert(`✅ Acreditados ${cap.data.credited_oros || 0} oros. Saldo: ${cap.data.balance}`);
          } catch (e) {
            alert("La orden quedo pendiente. Intentaras de nuevo desde 'Mis ordenes'.");
          }
        }
      }, 1500);
    } catch (e) { setErr(formatError(e)); }
  };

  const getAgent = (id) => agents.find((a) => a.id === id);
  const currentAgent = activeSession ? getAgent(activeSession.agent_id) : null;

  // Pushea UNA app a su repo dedicado — reutiliza /me/github/push-app existente
  const _pushAppDedicated = async (appSlug) => {
    const msg = prompt(
      "Mensaje del commit (opcional):",
      `deploy: ${appSlug} ${new Date().toLocaleString()}`
    );
    if (msg === null) return;
    try {
      const { data } = await api.post("/me/github/push-app", {
        app_slug: appSlug,
        create_new: true,
        commit_message: msg,
      });
      if (data.ok) {
        const repoUrl = data.repo_url || `https://github.com/${data.repo}`;
        const repoLine = data.created_new_repo
          ? `\n✨ Repo nuevo creado: ${data.repo}`
          : `\nRepo: ${data.repo}`;
        alert(`✅ Push exitoso!${repoLine}\n\nVer en GitHub: ${repoUrl}`);
      } else if (data.export_locked) {
        if (window.confirm(`🔒 ${data.message}\n\n¿Querés recargar oros ahora?`))
          window.location.hash = "#/recharge";
      } else {
        const lastStep = (data.steps || []).slice(-1)[0];
        const detail =
          data.error ||
          data.message ||
          (lastStep && (lastStep.out || lastStep.err || JSON.stringify(lastStep))) ||
          "Error desconocido, contactá soporte.";
        alert(`⚠ Push falló:\n\n${detail}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || formatError(e);
      if (detail?.toLowerCase().includes("token") || detail?.toLowerCase().includes("configura")) {
        setShowGitHubSetup(true);
      } else {
        alert(`✕ ${detail}`);
      }
    }
  };

  // Botón "Push a GitHub" del header: lista apps primero, luego pushea per-app
  const pushNow = async () => {
    try {
      const { data } = await api.get("/me/apps");
      const apps = data.apps || [];
      if (apps.length === 0) {
        alert("No tenés apps generadas. Creá una primero con App Builder Pro.");
        return;
      }
      if (apps.length === 1) {
        await _pushAppDedicated(apps[0].name);
      } else {
        setAppPickerForPush(apps);
      }
    } catch (e) {
      setErr(formatError(e));
    }
  };

  return (
    <div className={`boss-console ls-v2-theme${previewOpen ? " boss-console--split" : ""}${previewFullscreen ? " boss-console--preview-fs" : ""}${mobileTabOpen && previewOpen ? " boss-console--mobile-tab" : ""}`} data-testid="boss-console">
      <aside className="bc-sidebar">
        <div className="bc-side-head">
          <button className="bc-new-btn bc-new-project-btn"
                  onClick={() => setShowNewProjectModal(true)}
                  data-testid="bc-new-project-btn">
            + Nuevo proyecto
          </button>
        </div>

        <div className="bc-thread-list">
          {/* ── PROJECTS section ─────────────────────────────────────────── */}
          {userProjects.length > 0 && (
            <>
              <div className="bc-section-label">Proyectos</div>
              {userProjects.map((proj) => {
                const isActive = proj.session_id && activeId === proj.session_id;
                const fileCount = proj.files_created?.length || 0;
                const isLive    = proj.phase === "preview" || proj.phase === "done";
                const isBuilding = ["generating","fixing","validating"].includes(proj.phase);
                return (
                  <div key={proj.id}
                       className={`bc-project-card${isActive ? " active" : ""}`}
                       onClick={() => selectProject(proj)}
                       data-testid={`bc-project-${proj.id}`}>
                    <div className="bc-project-card-header">
                      <span className="bc-project-card-name">{proj.name}</span>
                      <div style={{ display:"flex", alignItems:"center", gap:"0.25rem" }}>
                        {isBuilding && <span style={{ fontSize:"0.55rem", color:"var(--accent)" }}>⟳</span>}
                        {isLive && proj.preview_url && (
                          <span style={{ fontSize:"0.55rem", color:"#22c55e" }}>●</span>
                        )}
                        <span className={`bc-phase-badge bc-phase-badge--${proj.phase}`}
                              style={{ fontSize: "0.6rem", padding: "0.1rem 0.35rem" }}>
                          {PHASE_LABELS[proj.phase] || proj.phase}
                        </span>
                        <button className="bc-thread-del"
                                title="Eliminar proyecto"
                                onClick={(e) => deleteProject(e, proj.id)}>×</button>
                      </div>
                    </div>
                    {proj.description && (
                      <div className="bc-project-card-desc">{proj.description.slice(0, 60)}</div>
                    )}
                    <div className="bc-project-card-meta">
                      {proj.stack?.backend && <span>{proj.stack.backend.split(" ")[0]}</span>}
                      {proj.stack?.database && <span>{proj.stack.database.split(" ")[0]}</span>}
                      {fileCount > 0 && <span>{fileCount} archivos</span>}
                      {!proj.session_id && <span style={{color:"var(--text-muted)", fontSize:"0.6rem"}}>sin sesión</span>}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* ── CONVERSATIONS section ─────────────────────────────────────── */}
          {sessions.filter(s => !s.project_id).length > 0 && (
            <>
              <div className="bc-section-label" style={{ marginTop: userProjects.length ? "0.75rem" : 0 }}>
                Conversaciones
              </div>
              {sessions.filter(s => !s.project_id).map((s) => (
                <div key={s.id} className={`bc-thread ${activeId === s.id ? "active" : ""}`}
                     onClick={() => setActiveId(s.id)} data-testid={`bc-thread-${s.id}`}>
                  <div className="ls-v2-thread-robot"><LuiviaRobot size={26} /></div>
                  <div className="bc-thread-meta">
                    <div className="bc-thread-title">{s.title}</div>
                    <div className="bc-thread-preview">{s.last_message_preview || "Sin mensajes"}</div>
                  </div>
                  <button className="bc-thread-del"
                          onClick={(e) => { e.stopPropagation(); delSession(s.id); }}>×</button>
                </div>
              ))}
            </>
          )}

          {sessions.length === 0 && userProjects.length === 0 && (
            <div className="bc-empty">Crea tu primer proyecto ↑</div>
          )}
        </div>

        {/* ── New Project Modal ──────────────────────────────────────────── */}
        {showNewProjectModal && (
          <div className="bc-modal-overlay" onClick={() => setShowNewProjectModal(false)}>
            <div className="bc-new-project-modal" onClick={(e) => e.stopPropagation()}
                 data-testid="bc-new-project-modal">
              <div className="bc-modal-header">
                <span>Nuevo proyecto</span>
                <button className="bc-modal-close" onClick={() => setShowNewProjectModal(false)}>×</button>
              </div>
              <div className="bc-modal-body">
                <label className="bc-modal-label">Nombre del proyecto *</label>
                <input
                  className="bc-modal-input"
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="ej: BarberBooking, MiTienda, TaskApp"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && createProject()}
                  data-testid="bc-new-project-name"
                />
                <label className="bc-modal-label">Descripción (opcional)</label>
                <textarea
                  className="bc-modal-input"
                  value={newProjectDesc}
                  onChange={(e) => setNewProjectDesc(e.target.value)}
                  placeholder="¿Qué hace esta app? ¿Para quién es?"
                  rows={3}
                  data-testid="bc-new-project-desc"
                />
                <button
                  className="bc-modal-submit"
                  onClick={createProject}
                  disabled={creatingProject || !newProjectName.trim()}
                  data-testid="bc-new-project-submit"
                >
                  {creatingProject ? "Creando..." : "✓ Crear proyecto"}
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>

      <main className="bc-main">
        <header className="bc-header">
          <div className="bc-header-left">
            <div className="ls-v2-header-robot">
              <LuiviaRobot size={38} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="bc-header-name">Lluvia Studio</div>
              <div className="bc-header-tag" style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                <span>{activeSession ? (activeSession.title?.slice(0,38) || "Conversación activa") : "IA que construye aplicaciones"}</span>
                {activeProject && (
                  <span className={`bc-phase-badge bc-phase-badge--${activeProject.phase}`}>
                    {PHASE_LABELS[activeProject.phase] || activeProject.phase}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="bc-header-right">
            <div className="bc-credits ls-v2-credits-badge" data-testid="bc-credits"
                 onClick={() => setShowShop(true)} title="Ver packs de oros" style={{ cursor: "pointer" }}>
              <span className="bc-credits-icon">⚜</span>
              <span className="bc-credits-num">{balance ?? "—"}</span>
              <span className="bc-credits-label">oros</span>
            </div>
          </div>
        </header>

        {err && <div className="alert" data-testid="bc-error">{err}</div>}

        <div
          className={`bc-chat ${dragOver ? "bc-drag-over" : ""}`}
          ref={scrollRef}
          onDragOver={(e) => { if (activeSession) { e.preventDefault(); setDragOver(true); } }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          {dragOver && activeSession && (
            <div className="bc-drop-hint" data-testid="bc-drop-hint">
              <span>📷 Soltá la imagen para enviarla</span>
            </div>
          )}
          {!activeSession && (
            <div className="ls-v2-welcome">
              <div className="ls-v2-robot-wrap">
                <LuiviaRobot size={100} className="ls-v2-robot-hero" />
              </div>
              <div className="ls-v2-hero-eyebrow">Lluvia Studio</div>
              <h1 className="ls-v2-hero-title">¿Qué quieres construir hoy?</h1>
              <p className="ls-v2-hero-sub">Tu arquitecto de aplicaciones impulsado por IA.<br/>Describe tu idea y me encargo del resto.</p>
              <div className="ls-v2-chips-wrap">
                {[
                  "Crear una app móvil",
                  "Crear una landing page",
                  "Crear un CRM",
                  "Crear una tienda online",
                  "Crear un agente IA",
                  "Crear una plataforma SaaS",
                ].map(t => (
                  <button key={t} className="ls-v2-chip" onClick={() => send(t)} data-testid={`ls-chip-${t}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeSession?.messages?.map((m) => (
            <Message key={m.id} msg={m} agent={currentAgent} onPlay={playTts} backendBase={backendBase} />
          ))}

          {buildBubbles.map((b) => (
            <div key={b.id} className="bc-msg bc-msg-assistant bc-msg-build-bubble">
              <div className="ls-v2-msg-robot" aria-hidden="true">
                <LuiviaRobot size={28} />
              </div>
              <div className="bc-msg-body">
                <div className="bc-build-bubble-content">
                  <span>{b.content}</span>
                  {b.pct != null && b.pct < 100 && (
                    <div className="bc-build-bubble-bar">
                      <div className="bc-build-bubble-bar-fill" style={{ width: `${b.pct}%` }} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {(sending || ["generating","fixing","validating"].includes(activeProject?.phase)) && (
            <div className="bc-msg bc-msg-assistant">
              <div className="ls-v2-msg-robot" aria-hidden="true">
                <LuiviaRobot size={28} />
              </div>
              <div className="bc-msg-body">
                <div className="bc-typing-dots"><span/><span/><span/></div>
              </div>
            </div>
          )}

          {activeProject && (
            <BuildAutoProgress
              project={activeProject}
              liveEvents={buildLiveEvents}
              onPreview={(slug) => {
                if (slug) setPreviewSlug(slug);
                setPreviewEverOpened(true);
                setPreviewOpen(true);
              }}
            />
          )}

          {/* B6-3/B6-4: SnapshotPanel — historial, stable, clone */}
          {activeProject?.app_slug && (
            <SnapshotPanel
              appSlug={activeProject.app_slug}
              backendBase={backendBase}
              projectFrozen={!!activeProject.frozen}
            />
          )}

          {activeBootstrapServerId && (
            <InfraProgress liveEvents={infraLiveEvents} />
          )}
        </div>

        {/* ── Preview Bar — estilo Emergent: acceso inferior al preview ── */}
        {activeSession && (previewSlug || activeProject?.preview_url || userApps.length > 0) && (
          <div className="ls-v2-preview-bar">
            <button
              className={`ls-v2-preview-pill${previewOpen ? " active" : ""}`}
              onClick={() => {
                if (!previewOpen) {
                  const slug = previewSlug || activeProject?.app_slug || detectPreviewSlug(activeSession?.messages) || userApps[0]?.name;
                  setPreviewSlug(slug || null);
                  setPreviewEverOpened(true);
                }
                setPreviewOpen(o => !o);
              }}
              data-testid="bc-preview-toggle"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <span>{previewOpen ? "Cerrar preview" : "Vista previa en vivo"}</span>
              {!previewOpen && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M7 17L17 7M7 7h10v10"/>
                </svg>
              )}
            </button>
            {previewOpen && (
              <button
                className={`ls-v2-preview-pill ls-v2-preview-fs${previewFullscreen ? " active" : ""}`}
                onClick={() => setPreviewFullscreen(f => !f)}
                title={previewFullscreen ? "Salir fullscreen" : "Pantalla completa"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  {previewFullscreen
                    ? <><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></>
                    : <><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></>}
                </svg>
              </button>
            )}
            {isAdmin && (
              <button className="ls-v2-preview-pill ls-v2-preview-github"
                      onClick={pushNow} title="Push a GitHub" data-testid="bc-push-github">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 .297a12 12 0 0 0-3.79 23.39c.6.11.82-.26.82-.58v-2.02c-3.34.72-4.04-1.61-4.04-1.61-.55-1.4-1.35-1.78-1.35-1.78-1.1-.75.08-.74.08-.74 1.22.09 1.86 1.25 1.86 1.25 1.09 1.86 2.85 1.32 3.54 1.01.11-.79.42-1.32.77-1.62-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.31-.54-1.53.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.25 2.87.12 3.18.78.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 .297z"/>
                </svg>
                <span>GitHub</span>
              </button>
            )}
          </div>
        )}

        {/* ── FAB: mobile-only preview toggle ─────────────────────────── */}
        {previewSlug && previewOpen && !previewFullscreen && (
          <button
            className={`bc-mobile-preview-fab${mobileTabOpen ? " bc-mobile-preview-fab--back" : ""}`}
            onClick={() => setMobileTabOpen(t => !t)}
            aria-label={mobileTabOpen ? "Volver al chat" : "Ver preview"}
            data-testid="bc-mobile-fab"
          >
            {mobileTabOpen ? (
              /* ← back arrow */
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
            ) : (
              /* eye icon */
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
            {!mobileTabOpen && <span className="bc-fab-live-dot" />}
          </button>
        )}

        <div className="bc-composer-wrap">
            {attachments.length > 0 && (
              <div className="bc-attachments-row" data-testid="bc-attachments-row">
                {attachments.map((a) => (
                  <div key={a.id} className="bc-attachment-chip" data-testid="bc-attachment-chip">
                    <img src={a.preview} alt={a.name} />
                    {a.uploading && <div className="bc-attachment-uploading">…</div>}
                    <button
                      className="bc-attachment-remove"
                      onClick={() => removeAttachment(a.id)}
                      data-testid="bc-attachment-remove"
                      title="Quitar"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
            <div className="bc-composer">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                multiple
                style={{ display: "none" }}
                onChange={onFilePick}
                data-testid="bc-file-input"
              />
              <input
                ref={nativeCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={onFilePick}
                data-testid="bc-native-camera-input"
              />
              <button
                className="bc-attach-btn"
                onClick={() => setShowAttachMenu((v) => !v)}
                data-testid="bc-attach-btn"
                title="Adjuntar"
                disabled={sending}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
              </button>
              {showAttachMenu && (
                <div className="bc-attach-menu" data-testid="bc-attach-menu" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="bc-attach-menu-item"
                    onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }}
                    data-testid="bc-attach-gallery"
                  >
                    <span className="bc-attach-menu-icon">🖼</span>
                    <span>Subir desde galería</span>
                  </button>
                  <button
                    className="bc-attach-menu-item"
                    onClick={() => { setShowAttachMenu(false); openCamera(); }}
                    data-testid="bc-attach-camera"
                  >
                    <span className="bc-attach-menu-icon">📷</span>
                    <span>Tomar foto</span>
                  </button>
                  <button
                    className="bc-attach-menu-item"
                    onClick={() => { setShowAttachMenu(false); githubConfigured ? pushNow() : setShowGitHubSetup(true); }}
                    data-testid="bc-attach-github"
                  >
                    <span className="bc-attach-menu-icon">⬆</span>
                    <span>{githubConfigured ? "Push a GitHub" : "Conectar GitHub"}</span>
                  </button>
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPaste={onPaste}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }}}
                placeholder="Describe lo que quieres construir…"
                rows={1}
                data-testid="bc-input"
                disabled={sending} />
              <button
                className={`bc-mic-btn ${recording ? "rec" : ""}`}
                onClick={toggleRecord}
                data-testid="bc-mic-btn"
                title={recording ? "Detener grabación" : "Hablar al agente"}>
                {recording ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                )}
              </button>
              <button
                className="bc-send-btn"
                onClick={() => send()}
                disabled={(!input.trim() && attachments.filter(a => a.url).length === 0) || sending || attachments.some(a => a.uploading)}
                data-testid="bc-send-btn">
                {sending ? (
                  <span className="bc-send-spinner"/>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7"/>
                  </svg>
                )}
              </button>
            </div>
            <div className="bc-composer-hint">
              Adjunta imágenes para que Lluvia Studio analice tu proyecto
            </div>
          </div>
      </main>

      {/* Modal: agent picker */}
      {showPicker && (
        <div className="bc-modal-overlay" onClick={() => setShowPicker(false)}>
          <div className="bc-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Elige un agente</h3>
            <div className="bc-agent-grid">
              {agents.filter(a => a.id === STUDIO_AGENT_ID).map((a) => (
                <button key={a.id} className="bc-agent-card"
                        onClick={() => createSession(a.id)}>
                  <AgentAvatar agent={a} size={44} rounded="rounded" />
                  <div className="bc-agent-name">{a.name}</div>
                  <div className="bc-agent-tag">{a.tagline}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal: PayPal shop */}
      {showShop && (
        <div className="bc-modal-overlay" onClick={() => setShowShop(false)}>
          <div className="bc-modal" onClick={(e) => e.stopPropagation()} data-testid="bc-shop-modal">
            <h3>Recargar oros</h3>
            {!packsConfigured ? (
              <div className="alert">
                PayPal no configurado todavia.<br/>
                Pega tus credenciales en <code>backend/.env</code>:<br/>
                <code>PAYPAL_CLIENT_ID=...</code><br/>
                <code>PAYPAL_SECRET=...</code><br/>
                Y reinicia el backend.
              </div>
            ) : (
              <div className="bc-pack-grid">
                {Object.entries(packs).map(([k, p]) => (
                  <button key={k} className="bc-pack-card" onClick={() => buyPack(k)}
                          data-testid={`bc-pack-${k}`}>
                    <div className="bc-pack-oros">{p.oros.toLocaleString()} ⚜</div>
                    <div className="bc-pack-price">${p.price_usd} USD</div>
                    <div className="bc-pack-label">{p.label}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Cámara nativa (getUserMedia) */}
      {cameraOpen && (
        <div className="bc-modal-overlay bc-camera-overlay" onClick={closeCamera}>
          <div className="bc-camera-modal" onClick={(e) => e.stopPropagation()} data-testid="bc-camera-modal">
            <div className="bc-camera-stage">
              <video
                ref={cameraVideoRef}
                className="bc-camera-video"
                autoPlay
                playsInline
                muted
                data-testid="bc-camera-video"
              />
              {cameraErr && (
                <div className="bc-camera-err" data-testid="bc-camera-err">
                  <div>{cameraErr}</div>
                  <button
                    className="bc-camera-fallback-btn"
                    onClick={triggerNativeCamera}
                    data-testid="bc-camera-native-btn"
                  >
                    📱 Usar cámara del teléfono
                  </button>
                </div>
              )}
            </div>
            <div className="bc-camera-controls">
              <button className="bc-camera-secondary" onClick={closeCamera} data-testid="bc-camera-cancel">
                Cancelar
              </button>
              <button
                className="bc-camera-shutter"
                onClick={capturePhoto}
                disabled={!!cameraErr}
                data-testid="bc-camera-shutter"
                aria-label="Capturar foto"
              >
                <span className="bc-camera-shutter-inner"/>
              </button>
              <button className="bc-camera-secondary" onClick={flipCamera} data-testid="bc-camera-flip" title="Voltear cámara">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 0 1 14.85-6.85L20 7M21 12a9 9 0 0 1-14.85 6.85L4 17"/>
                  <path d="M4 4v3h3M20 20v-3h-3"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* App picker: aparece cuando hay >1 apps y el usuario hace "Push a GitHub" */}
      {appPickerForPush && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setAppPickerForPush(null)}
        >
          <div
            style={{
              background: "var(--surface-card, #1a1a2e)", borderRadius: 14,
              padding: "1.4rem", minWidth: 320, maxWidth: 460,
              boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "0.35rem" }}>
              ¿Qué app pushear a GitHub?
            </div>
            <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "1rem", lineHeight: 1.45 }}>
              Cada app va a su propio repo — nunca se mezclan entre sí.
            </div>
            {appPickerForPush.map(app => (
              <button
                key={app.name}
                data-testid={`push-picker-${app.name}`}
                onClick={() => { setAppPickerForPush(null); _pushAppDedicated(app.name); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "0.65rem 0.9rem", marginBottom: "0.45rem",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 9, cursor: "pointer",
                  color: "var(--text-primary)", fontWeight: 600, fontSize: "0.9rem",
                }}
              >
                📦 {app.name}
                {app.size_bytes > 0 && (
                  <span style={{ fontSize: "0.72rem", fontWeight: 400, color: "var(--text-muted)", marginLeft: "0.5rem" }}>
                    {Math.round(app.size_bytes / 1024)} KB
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={() => setAppPickerForPush(null)}
              style={{
                marginTop: "0.3rem", width: "100%", padding: "0.5rem",
                background: "transparent", border: "none",
                color: "var(--text-muted)", cursor: "pointer", fontSize: "0.82rem",
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal: GitHub setup inline */}
      {showGitHubSetup && (
        <GitHubSetupModal
          onClose={() => setShowGitHubSetup(false)}
          onSaved={() => {
            setGithubConfigured(true);
            setShowGitHubSetup(false);
            pushNow();
          }}
        />
      )}

      {/* ── Live Preview Panel — right pane of the split layout ── */}
      {!!previewSlug && (
        <div
          className={`bc-preview-pane${!previewOpen ? " bc-preview-pane--hidden" : ""}${previewFullscreen ? " bc-preview-pane--fullscreen" : ""}${mobileTabOpen ? " bc-preview-pane--mobile-tab" : ""}`}
          data-testid="bc-preview-pane"
        >
          {previewSlug ? (
            <PreviewPanel
              key={previewSlug}
              appSlug={previewSlug}
              autoStart={true}
              onClose={() => { setPreviewOpen(false); setPreviewFullscreen(false); }}
            />
          ) : (
            <div className="preview-panel">
              <div className="preview-header">
                <span style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>Preview</span>
                <button className="preview-btn" onClick={() => setPreviewOpen(false)}>✕</button>
              </div>
              <div className="preview-empty">
                <span style={{ fontSize: "2rem", opacity: 0.3, marginBottom: "1rem" }}>👁</span>
                <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                  Sin app seleccionada
                </p>
                {userApps.length > 0 && (
                  <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <p style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Tus apps:</p>
                    {userApps.slice(0, 5).map(app => (
                      <button key={app.name} className="cta-secondary"
                              onClick={() => setPreviewSlug(app.name)}
                              style={{ fontSize: "0.85rem" }}>
                        ▶ {app.name}
                      </button>
                    ))}
                  </div>
                )}
                {userApps.length === 0 && (
                  <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                    Pídele a E1 que genere una app primero
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GitHubSetupModal({ onClose, onSaved }) {
  const [token, setToken] = useState("");
  const [repo, setRepo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!token.trim() || !repo.trim()) { setErr("Token y repositorio son obligatorios."); return; }
    setBusy(true); setErr("");
    try {
      await api.put("/me/settings", { github_token: token.trim(), github_repo: repo.trim() });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error guardando. Intentá de nuevo.");
    } finally { setBusy(false); }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 300,
               display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
      onClick={onClose}
    >
      <div
        style={{ background: "var(--surface-card, #1a1a2e)", borderRadius: 16, padding: "1.5rem",
                 width: "100%", maxWidth: 420, boxShadow: "0 16px 56px rgba(0,0,0,0.55)" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: "1.05rem", letterSpacing: "-0.01em" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: "middle", marginRight: 6 }}>
                <path d="M12 .297a12 12 0 0 0-3.79 23.39c.6.11.82-.26.82-.58v-2.02c-3.34.72-4.04-1.61-4.04-1.61-.55-1.4-1.35-1.78-1.35-1.78-1.1-.75.08-.74.08-.74 1.22.09 1.86 1.25 1.86 1.25 1.09 1.86 2.85 1.32 3.54 1.01.11-.79.42-1.32.77-1.62-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.31-.54-1.53.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.25 2.87.12 3.18.78.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 .297z"/>
              </svg>
              Conectar GitHub
            </div>
            <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: 2 }}>
              Solo se necesita una vez
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "transparent", border: 0, fontSize: "1.4rem", cursor: "pointer",
                     color: "var(--text-muted)", lineHeight: 1, padding: "0 0.2rem" }}>×</button>
        </div>

        <div style={{ marginBottom: "0.75rem" }}>
          <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600,
                          color: "var(--text-secondary)", marginBottom: "0.35rem" }}>
            Personal Access Token
          </label>
          <input
            type="password"
            value={token}
            onChange={e => setToken(e.target.value)}
            placeholder="ghp_..."
            autoFocus
            style={{ width: "100%", padding: "0.6rem 0.75rem", borderRadius: 9, fontSize: "0.9rem",
                     border: "1px solid var(--border)", background: "var(--surface)",
                     color: "var(--text-primary)", boxSizing: "border-box" }}
          />
          <a href="https://github.com/settings/tokens/new?scopes=repo&description=Lluvia+App+Studio"
             target="_blank" rel="noreferrer"
             style={{ fontSize: "0.75rem", color: "var(--brand-primary)", marginTop: "0.3rem", display: "inline-block" }}>
            Crear token en GitHub (scope: repo) →
          </a>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600,
                          color: "var(--text-secondary)", marginBottom: "0.35rem" }}>
            Repositorio destino
          </label>
          <input
            value={repo}
            onChange={e => setRepo(e.target.value)}
            placeholder="usuario/mi-repo"
            style={{ width: "100%", padding: "0.6rem 0.75rem", borderRadius: 9, fontSize: "0.9rem",
                     border: "1px solid var(--border)", background: "var(--surface)",
                     color: "var(--text-primary)", boxSizing: "border-box" }}
          />
        </div>

        {err && (
          <div style={{ padding: "0.55rem 0.75rem", borderRadius: 8, background: "rgba(239,68,68,0.1)",
                        border: "1px solid rgba(239,68,68,0.3)", color: "#EF4444",
                        fontSize: "0.83rem", marginBottom: "0.75rem" }}>
            {err}
          </div>
        )}

        <button
          onClick={save}
          disabled={busy}
          style={{ width: "100%", padding: "0.75rem", borderRadius: 10, fontWeight: 700,
                   fontSize: "0.95rem", border: 0, cursor: busy ? "wait" : "pointer",
                   background: "linear-gradient(135deg,#2563EB,#7C3AED)", color: "#fff",
                   opacity: busy ? 0.7 : 1 }}
        >
          {busy ? "Guardando..." : "Conectar y hacer Push →"}
        </button>
      </div>
    </div>
  );
}

function Message({ msg, agent, onPlay, backendBase }) {
  const isUser = msg.role === "user";
  const imageUrls = msg.image_urls || [];
  const absUrl = (u) => {
    if (!u) return u;
    if (u.startsWith("http") || u.startsWith("blob:") || u.startsWith("data:")) return u;
    return (backendBase || "") + u;
  };
  // Extraer rich cards desde tool_calls
  const RICH_CARD_TOOLS = [
    "paypal_invoice_card", "service_card", "push_to_my_github",
    "generate_haircut_preview", "video_script_card", "generate_promo_video",
    // B6-2: Constructor Autónomo
    "present_build_plan", "use_template",
  ];
  const cards = (msg.tool_calls || []).map((tc) => {
    if (!RICH_CARD_TOOLS.includes(tc.name)) return null;
    try {
      const r = JSON.parse(tc.result_preview || "{}");
      if (r.card_type) return r;
    } catch (_) {}
    return null;
  }).filter(Boolean);

  return (
    <div className={`bc-msg ${isUser ? "bc-msg-user" : "bc-msg-assistant"}`} data-testid={`bc-msg-${msg.role}`}>
      {!isUser && (
        <div className="ls-v2-msg-robot" aria-hidden="true">
          <LuiviaRobot size={28} />
        </div>
      )}
      <div className="bc-msg-body">
        {msg.tool_calls?.length > 0 && (
          <BuildProgress toolCalls={msg.tool_calls} />
        )}
        {imageUrls.length > 0 && (
          <div className={`bc-msg-images ${imageUrls.length > 1 ? "multi" : ""}`} data-testid="bc-msg-images">
            {imageUrls.map((u, i) => (
              <a key={i} href={absUrl(u)} target="_blank" rel="noreferrer" className="bc-msg-image-link">
                <img src={absUrl(u)} alt={`adjunto ${i+1}`} className="bc-msg-image" loading="lazy" />
              </a>
            ))}
          </div>
        )}
        {msg.content && <div className="bc-msg-text">{msg.content}</div>}
        {cards.map((c, i) => {
          if (c.card_type === "payment") return <PaymentCard key={i} card={c} agent={agent} />;
          if (c.card_type === "github_push") return <GitHubPushCard key={i} card={c} />;
          if (c.card_type === "before_after") return <BeforeAfterCard key={i} card={c} agent={agent} backendBase={backendBase} />;
          if (c.card_type === "video_script") return <VideoScriptCard key={i} card={c} agent={agent} />;
          if (c.card_type === "video_job") return <VideoJobCard key={i} card={c} agent={agent} backendBase={backendBase} />;
          if (c.card_type === "app_built") return <AppBuiltCard key={i} card={c} />;
          if (c.card_type === "build_plan") return <BuildPlanCard key={i} card={c} onConfirm={(msg) => send && send(msg)} />;
          return <ServiceCard key={i} card={c} agent={agent} />;
        })}
        {msg.superadmin_takeover && (
          <div className="bc-takeover-badge">👑 SuperAdmin · {msg.by}</div>
        )}
        <div className="bc-msg-foot">
          {msg.is_admin_free && msg.nominal_cost_oros > 0 && (
            <span className="bc-msg-cost" style={{ color: "#059669" }} title="Sos admin: no se cobra">
              👑 Gratis (admin · sería {msg.nominal_cost_oros} oros)
            </span>
          )}
          {!msg.is_admin_free && msg.cost_oros !== undefined && msg.cost_oros > 0 && (
            <span className="bc-msg-cost">-{msg.cost_oros} oros</span>
          )}
          {!isUser && msg.content && (
            <button className="bc-play-btn" onClick={() => onPlay(msg.content, agent?.voice)}
                    title="Escuchar">🔊</button>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentCard({ card, agent }) {
  const accent = agent?.color || "#5fb4ff";
  return (
    <div className="rich-card payment-card" data-testid="payment-card" style={{ borderColor: accent }}>
      <div className="rc-head" style={{ background: `linear-gradient(135deg, ${accent}22, transparent)` }}>
        <div className="rc-brand">
          <div className="rc-logo" style={{ background: accent }}>{(card.brand || "L").slice(0, 1)}</div>
          <div>
            <div className="rc-brand-name">{card.brand || "Lluvia App Studio"}</div>
            <div className="rc-brand-sub">Pago seguro · PayPal</div>
          </div>
        </div>
        <div className="rc-amount">${card.amount_usd}<small>USD</small></div>
      </div>
      <div className="rc-body">
        <div className="rc-desc">{card.description}</div>
        {card.client_name && <div className="rc-client">A nombre de: <strong>{card.client_name}</strong></div>}
        <div className="rc-order-id">Orden: {(card.order_id || "").slice(0, 12)}...</div>
      </div>
      <a href={card.approve_url} target="_blank" rel="noreferrer"
         className="rc-cta" style={{ background: accent }}
         data-testid="payment-card-cta">
        Pagar con PayPal →
      </a>
    </div>
  );
}

function ServiceCard({ card, agent }) {
  const accent = agent?.color || "#5fb4ff";
  return (
    <div className="rich-card service-card" data-testid="service-card" style={{ borderColor: accent }}>
      {card.image_url && (
        <img src={card.image_url} alt={card.title} className="rc-image" />
      )}
      <div className="rc-body">
        <div className="rc-title">{card.title}</div>
        {card.description && <div className="rc-desc">{card.description}</div>}
        {card.price_usd && (
          <div className="rc-price" style={{ color: accent }}>
            ${card.price_usd}<small> USD</small>
          </div>
        )}
        <button className="rc-cta-soft" style={{ borderColor: accent, color: accent }}>
          {card.cta_label || "Ver más"}
        </button>
      </div>
    </div>
  );
}

function GitHubPushCard({ card }) {
  const isOk = card.ok === true;
  const needsSetup = card.needs_setup === true;
  const exportLocked = card.export_locked === true;
  const stateColor = isOk ? "#059669" : (needsSetup || exportLocked) ? "#D97706" : "#DC2626";
  const stateLabel = isOk ? "Push exitoso" : exportLocked ? "🔒 Exportación bloqueada" : needsSetup ? "Setup pendiente" : "Push fallido";
  const stateIcon = isOk ? "✓" : exportLocked ? "🔒" : needsSetup ? "!" : "✕";
  // Si export_locked, mostramos card premium especial
  if (exportLocked) {
    return (
      <div className="rich-card github-push-card" data-testid="github-push-card-locked"
           style={{
             borderColor: "#D97706",
             background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(124,58,237,0.06))",
             overflow: "hidden",
           }}>
        <div className="rc-head" style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.18), rgba(124,58,237,0.10))" }}>
          <div className="rc-brand">
            <div className="rc-logo" style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)", fontSize: "1.05rem" }}>🔒</div>
            <div>
              <div className="rc-brand-name">Tu app está lista — falta desbloquear la exportación</div>
              <div className="rc-brand-sub">Vista previa premium · Lluvia App Studio</div>
            </div>
          </div>
        </div>
        <div className="rc-body">
          <div style={{ fontSize: "0.92rem", lineHeight: 1.55, color: "var(--text-secondary)" }}>
            {card.message}
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem",
            marginTop: "0.85rem", padding: "0.75rem", borderRadius: 12,
            background: "rgba(15,23,42,0.04)", border: "1px solid rgba(15,23,42,0.08)",
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Saldo</div>
              <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "var(--text-primary)" }}>{card.balance ?? 0}</div>
              <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>oros</div>
            </div>
            <div style={{ textAlign: "center", borderLeft: "1px solid rgba(15,23,42,0.1)", borderRight: "1px solid rgba(15,23,42,0.1)" }}>
              <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Necesitas</div>
              <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#D97706" }}>{card.required ?? 50}</div>
              <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>oros</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Te faltan</div>
              <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#7C3AED" }}>{card.missing ?? "?"}</div>
              <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>oros</div>
            </div>
          </div>
          {card.refunded_oros > 0 && (
            <div style={{ marginTop: "0.6rem", color: "#059669", fontWeight: 600, fontSize: "0.85rem" }}>
              💸 Te devolvimos {card.refunded_oros} oros del intento de push.
            </div>
          )}
        </div>
        <a href={card.recharge_url || "#/recharge"} className="rc-cta"
           style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)", display: "block", textAlign: "center" }}
           data-testid="github-push-card-recharge">
          Recargar oros y desbloquear exportación →
        </a>
      </div>
    );
  }
  return (
    <div className="rich-card github-push-card" data-testid="github-push-card"
         style={{ borderColor: stateColor }}>
      <div className="rc-head" style={{ background: `${stateColor}14` }}>
        <div className="rc-brand">
          <div className="rc-logo" style={{ background: stateColor, fontSize: "1rem" }}>{stateIcon}</div>
          <div>
            <div className="rc-brand-name">{stateLabel}</div>
            <div className="rc-brand-sub">Push a GitHub · Lluvia Workspace</div>
          </div>
        </div>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"
             style={{ color: "var(--text-primary)" }}>
          <path d="M12 .297a12 12 0 0 0-3.79 23.39c.6.11.82-.26.82-.58v-2.02c-3.34.72-4.04-1.61-4.04-1.61-.55-1.4-1.35-1.78-1.35-1.78-1.1-.75.08-.74.08-.74 1.22.09 1.86 1.25 1.86 1.25 1.09 1.86 2.85 1.32 3.54 1.01.11-.79.42-1.32.77-1.62-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.31-.54-1.53.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.25 2.87.12 3.18.78.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 .297z"/>
        </svg>
      </div>
      <div className="rc-body">
        {card.repo && (
          <div className="rc-desc">
            <span style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>Repositorio:</span>{" "}
            <strong>{card.repo}</strong>
            {card.branch && <span style={{ color: "var(--text-muted)" }}> · rama <code>{card.branch}</code></span>}
          </div>
        )}
        {card.commit_message && (
          <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: "0.35rem" }}>
            Commit: <em>{card.commit_message}</em>
          </div>
        )}
        {card.message && !isOk && (
          <div className="rc-desc" style={{ color: stateColor, marginTop: "0.5rem" }}>
            {card.message}
          </div>
        )}
        {card.error && (
          <div className="rc-desc" style={{ color: stateColor, marginTop: "0.5rem", whiteSpace: "pre-wrap", fontSize: "0.85rem", lineHeight: 1.55 }}>
            {card.error}
          </div>
        )}
        {card.refunded_oros > 0 && (
          <div style={{ marginTop: "0.6rem", color: "#059669", fontWeight: 600, fontSize: "0.88rem" }}>
            💸 Te reembolsamos {card.refunded_oros} oros automáticamente.
          </div>
        )}
      </div>
      {isOk && card.repo_url && (
        <a href={card.repo_url} target="_blank" rel="noreferrer"
           className="rc-cta" style={{ background: stateColor }}
           data-testid="github-push-card-cta">
          Ver en GitHub →
        </a>
      )}
      {needsSetup && (
        <a href="#/settings" className="rc-cta" style={{ background: stateColor }}
           data-testid="github-push-card-setup">
          Configurar GitHub ahora →
        </a>
      )}
      {card.help_url && !isOk && !needsSetup && (
        <a href={card.help_url} target="_blank" rel="noreferrer"
           className="rc-cta" style={{ background: stateColor }}
           data-testid="github-push-card-help">
          Crear token nuevo en GitHub →
        </a>
      )}
    </div>
  );
}

function BeforeAfterCard({ card, agent, backendBase }) {
  const accent = agent?.color || "#ec4899";
  const abs = (u) => {
    if (!u) return u;
    if (u.startsWith("http") || u.startsWith("blob:") || u.startsWith("data:")) return u;
    return (backendBase || "") + u;
  };
  const ok = card.ok === true;
  return (
    <div className="rich-card before-after-card" data-testid="before-after-card"
         style={{ borderColor: accent }}>
      <div className="rc-head" style={{ background: `linear-gradient(135deg, ${accent}22, transparent)` }}>
        <div className="rc-brand">
          <div className="rc-logo" style={{ background: accent }}>💇</div>
          <div>
            <div className="rc-brand-name">{card.look_name || "Look propuesto"}</div>
            <div className="rc-brand-sub">Estilista Visual · Vista previa IA</div>
          </div>
        </div>
      </div>
      {!ok && (
        <div className="rc-body">
          <div className="rc-desc" style={{ color: "#DC2626" }}>
            {card.error || "No pude generar la vista previa. Pedile al cliente una foto clara."}
          </div>
          {card.refunded_oros > 0 && (
            <div style={{ marginTop: "0.5rem", color: "#059669", fontSize: "0.85rem", fontWeight: 600 }}>
              💸 Te reembolsamos {card.refunded_oros} oros automáticamente.
            </div>
          )}
        </div>
      )}
      {ok && (
        <div className="ba-grid">
          <div className="ba-slot">
            <div className="ba-label">Antes</div>
            <a href={abs(card.before_url)} target="_blank" rel="noreferrer">
              <img src={abs(card.before_url)} alt="Antes" className="ba-img" loading="lazy" />
            </a>
          </div>
          <div className="ba-arrow" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7"/>
            </svg>
          </div>
          <div className="ba-slot">
            <div className="ba-label" style={{ color: accent }}>Después · IA</div>
            <a href={abs(card.after_url)} target="_blank" rel="noreferrer">
              <img src={abs(card.after_url)} alt="Después" className="ba-img"
                   style={{ borderColor: accent }} loading="lazy" />
            </a>
          </div>
        </div>
      )}
      {ok && card.look_description && (
        <div className="rc-body" style={{ paddingTop: "0.6rem" }}>
          <div className="rc-desc" style={{ fontSize: "0.82rem", color: "var(--text-muted)", fontStyle: "italic" }}>
            {card.look_description}
          </div>
        </div>
      )}
    </div>
  );
}

function VideoScriptCard({ card, agent }) {
  const accent = agent?.color || "#f59e0b";
  const platformLabel = {
    tiktok: "TikTok", reels: "Instagram Reels", shorts: "YouTube Shorts", todos: "9:16 universal",
  }[card.platform] || card.platform;
  const copyAll = () => {
    const text = [
      `📹 ${card.title}`,
      `Plataforma: ${platformLabel} · ${card.duration_sec}s`,
      ``,
      `HOOK (0-3s): ${card.hook}`,
      ``,
      `ESCENAS:`,
      ...(card.scenes || []).map((s) => `  ${s.t} · ${s.visual}\n      → "${s.voiceover}"`),
      ``,
      `CTA: ${card.cta}`,
      `Música: ${card.music_suggestion || "—"}`,
      ``,
      `📝 CAPTION:`,
      card.caption,
      ``,
      `# ${(card.hashtags || []).join(" ")}`,
    ].join("\n");
    navigator.clipboard?.writeText(text);
  };
  const requestRealVideo = () => {
    // Dispara un evento global que el contenedor (BossConsole) escucha y
    // arma un mensaje pre-rellenado en el composer para pedirle a Sora 2
    // el video real de este guion.
    const dur = card.duration_sec <= 6 ? 4 : (card.duration_sec <= 10 ? 8 : 12);
    const isHorizontal = card.platform === "shorts" || card.platform === "youtube";
    const aspectWord = isHorizontal ? "horizontal" : "vertical";
    const prompt = [
      `Hace el video REAL con Sora 2 de este guion:`,
      `"${card.title}". Hook: ${card.hook}.`,
      `Duracion ${dur} segundos, ${aspectWord}, calidad standard. Confirmo el costo.`,
    ].join(" ");
    window.dispatchEvent(new CustomEvent("lluvia:compose-message", { detail: { text: prompt, send: true } }));
  };
  return (
    <div className="rich-card video-script-card" data-testid="video-script-card"
         style={{ borderColor: accent }}>
      <div className="rc-head" style={{ background: `linear-gradient(135deg, ${accent}22, transparent)` }}>
        <div className="rc-brand">
          <div className="rc-logo" style={{ background: accent }}>🎬</div>
          <div>
            <div className="rc-brand-name">{card.title}</div>
            <div className="rc-brand-sub">{platformLabel} · {card.duration_sec}s</div>
          </div>
        </div>
        <button className="vs-copy-btn" onClick={copyAll}
                data-testid="video-script-copy" title="Copiar todo">
          📋
        </button>
      </div>
      <div className="vs-body">
        <div className="vs-section">
          <div className="vs-section-title">HOOK <span>(0-3s)</span></div>
          <div className="vs-hook" style={{ borderLeftColor: accent }}>{card.hook}</div>
        </div>
        <div className="vs-section">
          <div className="vs-section-title">ESCENAS</div>
          <ol className="vs-scenes">
            {(card.scenes || []).map((s, i) => (
              <li key={i} className="vs-scene">
                <span className="vs-scene-t" style={{ color: accent }}>{s.t}</span>
                <div className="vs-scene-visual"><strong>📷</strong> {s.visual}</div>
                <div className="vs-scene-vo">🎙 "{s.voiceover}"</div>
              </li>
            ))}
          </ol>
        </div>
        <div className="vs-section">
          <div className="vs-section-title">CTA</div>
          <div className="vs-cta" style={{ color: accent }}>{card.cta}</div>
        </div>
        {card.music_suggestion && (
          <div className="vs-section">
            <div className="vs-section-title">MÚSICA</div>
            <div className="vs-music">🎵 {card.music_suggestion}</div>
          </div>
        )}
        <div className="vs-section">
          <div className="vs-section-title">CAPTION</div>
          <div className="vs-caption">{card.caption}</div>
        </div>
        {card.hashtags?.length > 0 && (
          <div className="vs-section">
            <div className="vs-section-title">HASHTAGS</div>
            <div className="vs-hashtags">
              {card.hashtags.map((h, i) => (
                <span key={i} className="vs-hashtag" style={{ color: accent, borderColor: accent + "55" }}>
                  {h.startsWith("#") ? h : `#${h}`}
                </span>
              ))}
            </div>
          </div>
        )}
        <button
          className="vs-cta-real-video"
          onClick={requestRealVideo}
          style={{ background: accent }}
          data-testid="vs-request-real-video"
        >
          🎥 Generar este video REAL con Sora 2
          <span className="vs-cta-price">30–55 oros</span>
        </button>
      </div>
    </div>
  );
}



const AppBuiltCard = memo(function AppBuiltCard({ card }) {
  const ok = card.ok === true;
  const stateColor = ok ? "#059669" : "#DC2626";
  const accent = card.brand_color || "#5B8DEF";

  const [pushState, setPushState] = useState("idle"); // idle | pushing | done | err
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState("");
  const [repoName, setRepoName] = useState(card.repo_suggestion || card.app_slug || "");
  // activePanel: null | "deploy" | "preview"
  const [activePanel, setActivePanel] = useState(ok ? "preview" : null);

  const togglePanel = (panel) => setActivePanel(p => p === panel ? null : panel);

  const doPushAndDeploy = async () => {
    if (!repoName || repoName.length < 2) {
      setPushError("Elegí un nombre de repo (mínimo 2 caracteres).");
      return;
    }
    setPushState("pushing");
    setPushError("");
    try {
      const { data } = await api.post("/me/github/push-app", {
        app_slug: card.app_slug,
        repo_name: repoName,
        create_new: true,
        commit_message: `deploy: ${card.app_name || card.app_slug}`,
        set_as_default: false,
      });
      if (data.ok) {
        setPushResult(data);
        setPushState("done");
      } else {
        setPushError(data.error || data.message || "Push falló sin detalle");
        setPushState("err");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
      if (detail.toLowerCase().includes("token") || detail.toLowerCase().includes("configura")) {
        if (window.confirm("Falta tu token de GitHub. ¿Ir a configurarlo?")) {
          window.dispatchEvent(new CustomEvent("lluvia:goto-settings"));
        }
      }
      setPushError(detail);
      setPushState("err");
    }
  };

  return (
    <div className="rich-card app-built-card" data-testid="app-built-card"
         style={{ borderColor: stateColor, overflow: "hidden" }}>
      <div className="rc-head" style={{ background: `linear-gradient(135deg, ${accent}1F, ${accent}05)` }}>
        <div className="rc-brand">
          <div className="rc-logo" style={{ background: accent, fontSize: "1.1rem" }}>🚀</div>
          <div>
            <div className="rc-brand-name">{ok ? "App ensamblada" : "Falló el ensamble"}</div>
            <div className="rc-brand-sub">{card.template_name || "App Builder Pro"}</div>
          </div>
        </div>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600 }}>
          {card.stack}
        </div>
      </div>
      <div className="rc-body">
        {ok ? (
          <>
            {/* ── App name + meta ──────────────────────────────────────────── */}
            <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--text-primary)" }}>
              {card.app_name}
            </div>
            <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: "0.2rem" }}>
              Workspace: <code>{card.app_slug}</code> · {card.files_written} archivos · {Math.round((card.bytes_written || 0) / 1024)} KB
            </div>
            {(card.screens || []).length > 0 && (
              <div style={{
                marginTop: "0.9rem", display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)", gap: "0.45rem",
              }}>
                {(card.screens || []).map((s, i) => (
                  <div key={i} style={{
                    background: `${accent}10`, border: `1px solid ${accent}33`,
                    borderRadius: 10, padding: "0.5rem 0.7rem", fontSize: "0.82rem",
                    fontWeight: 600, color: "var(--text-primary)",
                  }}>
                    📱 {s}
                  </div>
                ))}
              </div>
            )}
            {card.next_step && (
              <div style={{
                marginTop: "0.65rem", padding: "0.55rem 0.75rem",
                background: "var(--surface-muted, rgba(0,0,0,0.04))",
                borderRadius: 10, fontSize: "0.82rem", lineHeight: 1.5,
                color: "var(--text-secondary)",
              }}>
                💡 {card.next_step}
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                HERO PREVIEW — "El producto es el protagonista"
                Desktop: preview siempre visible, auto-arranca
                Mobile:  CTA grande → abre preview al tocar
                ══════════════════════════════════════════════════════════ */}

            {/* Desktop hero: PreviewPanel siempre visible */}
            <div className="app-preview-hero" data-testid="app-preview-hero">
              <PreviewPanel
                appSlug={card.app_slug}
                autoStart={true}
                inline={true}
              />
            </div>

            {/* Mobile CTA: botón grande en lugar del hero */}
            {activePanel !== "preview" && (
              <button
                className="app-preview-mobile-cta"
                onClick={() => setActivePanel("preview")}
                data-testid="app-built-preview-cta-mobile"
                style={{ "--card-accent": accent }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                Ver tu app en vivo
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M7 17L17 7M7 7h10v10"/>
                </svg>
              </button>
            )}
            {/* Mobile: panel cuando CTA fue clickeado */}
            {activePanel === "preview" && (
              <div className="app-preview-mobile-panel">
                <PreviewPanel
                  appSlug={card.app_slug}
                  autoStart={true}
                  inline={true}
                  onClose={() => setActivePanel(null)}
                />
              </div>
            )}

            {/* ── Secondary actions: Deploy + GitHub (debajo del preview) ──── */}
            <div className="app-secondary-actions" style={{ "--card-accent": accent }}
                 data-testid="app-secondary-actions">
              {/* Deploy */}
              <button
                className={`app-sec-btn app-sec-btn--deploy${activePanel === "deploy" ? " active" : ""}`}
                onClick={() => setActivePanel(p => p === "deploy" ? null : "deploy")}
                data-testid="app-built-deploy-btn"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                {pushState === "done" ? "Deployed ✓" : pushState === "pushing" ? "Pushing…" : "Deploy"}
              </button>

              {/* GitHub */}
              <button
                className="app-sec-btn app-sec-btn--github"
                onClick={() => {
                  if (pushState === "done" && pushResult?.repo_url) {
                    window.open(pushResult.repo_url, "_blank");
                  } else {
                    setActivePanel(p => p === "deploy" ? null : "deploy");
                  }
                }}
                data-testid="app-built-github-btn"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 .297a12 12 0 0 0-3.79 23.39c.6.11.82-.26.82-.58v-2.02c-3.34.72-4.04-1.61-4.04-1.61-.55-1.4-1.35-1.78-1.35-1.78-1.1-.75.08-.74.08-.74 1.22.09 1.86 1.25 1.86 1.25 1.09 1.86 2.85 1.32 3.54 1.01.11-.79.42-1.32.77-1.62-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.31-.54-1.53.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.25 2.87.12 3.18.78.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 .297z"/>
                </svg>
                {pushState === "done" ? "Ver repo" : "GitHub"}
              </button>
            </div>

            {/* ── Deploy panel inline (aparece debajo de secondary actions) ── */}
            {activePanel === "deploy" && (
              <div className="app-deploy-inline" data-testid="app-built-deploy-panel">
                {pushState === "done" && pushResult ? (
                  <div className="app-deploy-result">
                    <div style={{ fontWeight: 700, color: "#059669", fontSize: "0.9rem" }}>
                      ✅ Subido correctamente
                    </div>
                    <code style={{ fontSize: "0.8rem", opacity: 0.7 }}>{pushResult.repo}</code>
                    <div className="app-deploy-result-links">
                      <a href={pushResult.repo_url} target="_blank" rel="noreferrer"
                         className="app-deploy-link app-deploy-link--github"
                         data-testid="app-built-repo-link">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .297a12 12 0 0 0-3.79 23.39c.6.11.82-.26.82-.58v-2.02c-3.34.72-4.04-1.61-4.04-1.61-.55-1.4-1.35-1.78-1.35-1.78-1.1-.75.08-.74.08-.74 1.22.09 1.86 1.25 1.86 1.25 1.09 1.86 2.85 1.32 3.54 1.01.11-.79.42-1.32.77-1.62-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.31-.54-1.53.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.25 2.87.12 3.18.78.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 .297z"/></svg>
                        Ver en GitHub
                      </a>
                      <a href={pushResult.render_deploy_url || `https://render.com/deploy?repo=${encodeURIComponent(pushResult.repo_url)}`}
                         target="_blank" rel="noreferrer"
                         className="app-deploy-link app-deploy-link--render"
                         data-testid="app-built-deploy-render">
                        ⚡ Deploy a Render
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Header — deja claro que se crea un repo NUEVO */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem",
                                  fontWeight: 700, fontSize: "0.88rem", color: "var(--v2-text, #fff)" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                        <path d="M12 .297a12 12 0 0 0-3.79 23.39c.6.11.82-.26.82-.58v-2.02c-3.34.72-4.04-1.61-4.04-1.61-.55-1.4-1.35-1.78-1.35-1.78-1.1-.75.08-.74.08-.74 1.22.09 1.86 1.25 1.86 1.25 1.09 1.86 2.85 1.32 3.54 1.01.11-.79.42-1.32.77-1.62-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.31-.54-1.53.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.25 2.87.12 3.18.78.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 .297z"/>
                      </svg>
                      Crear nuevo repositorio en GitHub
                    </div>
                    <div className="app-deploy-hint">
                      Se creará <strong>github.com/tu-usuario/</strong>{repoName || "nombre-del-repo"} — repositorio dedicado solo para esta app.
                    </div>
                    <input
                      type="text" value={repoName}
                      onChange={(e) => setRepoName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                      placeholder="nombre-del-repo"
                      data-testid="app-built-repo-name"
                      autoFocus
                    />
                    <div className="app-deploy-inline-row">
                      <button
                        className="app-deploy-confirm"
                        onClick={doPushAndDeploy}
                        disabled={pushState === "pushing" || !repoName || repoName.length < 2}
                        data-testid="app-built-push-confirm"
                      >
                        {pushState === "pushing"
                          ? "⏳ Creando repo y subiendo código..."
                          : `🚀 Crear repo y publicar`}
                      </button>
                    </div>
                    {pushError && (
                      <div style={{ padding: "0.5rem 0.6rem", background: "rgba(220,38,38,0.12)",
                                    borderRadius: 8, fontSize: "0.8rem", color: "#DC2626", lineHeight: 1.4 }}
                           data-testid="app-built-push-err">
                        ⚠ {pushError}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </>

        ) : (
          <>
            <div style={{ color: stateColor, fontSize: "0.92rem", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {card.error || "Error desconocido"}
            </div>
            {card.refunded_oros > 0 && (
              <div style={{ marginTop: "0.6rem", color: "#059669", fontWeight: 600, fontSize: "0.88rem" }}>
                💸 Te reembolsamos {card.refunded_oros} oros automáticamente.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

// ── B6-3/B6-4: SnapshotPanel — historial, stable releases, clone ─────────────
function SnapshotPanel({ appSlug, backendBase, projectFrozen }) {
  const { token } = useAuth();
  const [open,        setOpen]        = useState(false);
  const [snapshots,   setSnapshots]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [creating,    setCreating]    = useState(false);
  const [restoring,   setRestoring]   = useState(null);
  const [confirmSnap, setConfirmSnap] = useState(null);
  const [cloningSnap, setCloningSnap] = useState(null); // snap being cloned
  const [cloneInput,  setCloneInput]  = useState("");
  const [togglingStable, setTogglingStable] = useState(null);
  const [err,         setErr]         = useState(null);
  const [labelInput,  setLabelInput]  = useState("");

  const base = (backendBase || "") + "/api";

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${base}/me/apps/${appSlug}/snapshots`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setSnapshots(d.snapshots || []);
    } catch { setErr("No se pudieron cargar las versiones"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open, appSlug]);

  const handleCreate = async () => {
    setCreating(true); setErr(null);
    try {
      const r = await fetch(`${base}/me/apps/${appSlug}/snapshots`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ label: labelInput.trim() }),
      });
      const d = await r.json();
      if (!d.ok) { setErr(d.error || "Error creando versión"); return; }
      setLabelInput(""); await load();
    } catch { setErr("Error de red"); }
    finally { setCreating(false); }
  };

  const handleRestore = async (snap) => {
    if (confirmSnap?.id !== snap.id) { setConfirmSnap(snap); return; }
    setRestoring(snap.id); setConfirmSnap(null); setErr(null);
    try {
      const r = await fetch(`${base}/me/apps/${appSlug}/snapshots/${snap.id}/restore`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!d.ok && !d.restored_from) { setErr(d.detail || d.error || "Error restaurando"); return; }
      await load();
    } catch { setErr("Error de red"); }
    finally { setRestoring(null); }
  };

  const handleToggleStable = async (snap) => {
    setTogglingStable(snap.id);
    try {
      const r = await fetch(`${base}/me/apps/${appSlug}/snapshots/${snap.id}/stable`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setSnapshots(prev => prev.map(s => s.id === snap.id ? { ...s, is_stable: d.is_stable, stable_at: d.stable_at } : s));
    } catch { setErr("Error al cambiar estado stable"); }
    finally { setTogglingStable(null); }
  };

  const handleClone = async (snap) => {
    if (cloningSnap?.id !== snap.id) { setCloningSnap(snap); setCloneInput(`copia-${appSlug}`); return; }
    if (!cloneInput.trim()) return;
    setErr(null);
    try {
      const r = await fetch(`${base}/me/apps/${appSlug}/snapshots/${snap.id}/clone`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ new_app_name: cloneInput.trim() }),
      });
      const d = await r.json();
      if (!d.ok) { setErr(d.detail || d.error || "Error clonando"); return; }
      setErr(null);
      setCloningSnap(null);
      setCloneInput("");
    } catch { setErr("Error de red"); }
  };

  const handleDelete = async (snap) => {
    if (snap.is_stable) {
      if (!window.confirm("Este snapshot es Stable. ¿Confirmas borrarlo de todas formas?")) return;
      await fetch(`${base}/me/apps/${appSlug}/snapshots/${snap.id}?force=true`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      await fetch(`${base}/me/apps/${appSlug}/snapshots/${snap.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
    }
    setSnapshots(prev => prev.filter(s => s.id !== snap.id));
  };

  const stableCount = snapshots.filter(s => s.is_stable).length;

  const triggerLabel = (t) => ({
    manual: "manual", auto_pre_build: "auto·build", auto_pre_restore: "auto·restore",
  }[t] || t);
  const fmtSize = (b) => b > 1048576 ? `${(b/1048576).toFixed(1)}MB` : `${Math.round(b/1024)}KB`;
  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("es", { day:"2-digit", month:"short" }) + " " +
           d.toLocaleTimeString("es", { hour:"2-digit", minute:"2-digit" });
  };

  return (
    <div className="snp-panel">
      <button className="snp-toggle" onClick={() => setOpen(v => !v)}>
        <span className="snp-toggle-icon">{open ? "▾" : "▸"}</span>
        <span className="snp-toggle-label">Historial de versiones</span>
        {stableCount > 0 && <span className="snp-stable-count" title="versiones estables">⭐ {stableCount}</span>}
        {snapshots.length > 0 && !open && (
          <span className="snp-badge">{snapshots.length}</span>
        )}
      </button>

      {open && (
        <div className="snp-body">
          {/* [B6-4] Frozen banner */}
          {projectFrozen && (
            <div className="snp-frozen-banner">
              🔒 Proyecto congelado — reconstrucciones bloqueadas
            </div>
          )}

          {/* Crear snapshot */}
          <div className="snp-create-row">
            <input
              className="snp-label-input"
              placeholder="Etiqueta (opcional)"
              value={labelInput}
              onChange={e => setLabelInput(e.target.value)}
              maxLength={80}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
            />
            <button className="snp-create-btn" onClick={handleCreate} disabled={creating}>
              {creating ? "…" : "💾 Guardar versión"}
            </button>
          </div>

          {err && <div className="snp-err">{err}</div>}

          {loading ? (
            <div className="snp-loading">Cargando versiones…</div>
          ) : snapshots.length === 0 ? (
            <div className="snp-empty">Sin versiones guardadas aún.</div>
          ) : (
            <div className="snp-list">
              {snapshots.map(s => (
                <div key={s.id} className={`snp-item${s.is_stable ? " snp-item--stable" : ""}`}>
                  <div className="snp-item-main">
                    {s.is_stable && <span className="snp-stable-badge" title={`Stable desde ${s.stable_at ? fmtDate(s.stable_at) : ""}`}>⭐</span>}
                    <span className="snp-item-label">{s.label}</span>
                    <span className={`snp-trigger snp-trigger--${(s.trigger||"manual").replace(/_/g,"-")}`}>
                      {triggerLabel(s.trigger)}
                    </span>
                  </div>
                  <div className="snp-item-meta">
                    <span>{fmtDate(s.created_at)}</span>
                    <span>·</span>
                    <span>{s.file_count} archivos · {fmtSize(s.size_bytes)}</span>
                  </div>

                  {/* Clone input inline */}
                  {cloningSnap?.id === s.id && (
                    <div className="snp-clone-row">
                      <input
                        className="snp-label-input"
                        placeholder="Nombre de la nueva app"
                        value={cloneInput}
                        onChange={e => setCloneInput(e.target.value)}
                        maxLength={60}
                        autoFocus
                        onKeyDown={e => e.key === "Enter" && handleClone(s)}
                      />
                      <button className="snp-btn snp-btn--confirm" onClick={() => handleClone(s)}>Clonar</button>
                      <button className="snp-btn snp-btn--cancel" onClick={() => setCloningSnap(null)}>×</button>
                    </div>
                  )}

                  <div className="snp-item-actions">
                    {confirmSnap?.id === s.id ? (
                      <>
                        <span className="snp-confirm-text">¿Restaurar? Se guardará el estado actual primero.</span>
                        <button className="snp-btn snp-btn--confirm" onClick={() => handleRestore(s)} disabled={restoring === s.id}>
                          {restoring === s.id ? "Restaurando…" : "Confirmar"}
                        </button>
                        <button className="snp-btn snp-btn--cancel" onClick={() => setConfirmSnap(null)}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <button className="snp-btn snp-btn--restore" onClick={() => handleRestore(s)} disabled={!!restoring} title="Restaurar este workspace">
                          ↩ Restaurar
                        </button>
                        <button
                          className={`snp-btn snp-btn--stable${s.is_stable ? " active" : ""}`}
                          onClick={() => handleToggleStable(s)}
                          disabled={togglingStable === s.id}
                          title={s.is_stable ? "Quitar Stable" : "Marcar como Stable"}
                        >
                          {togglingStable === s.id ? "…" : (s.is_stable ? "⭐" : "☆")}
                        </button>
                        <button className="snp-btn snp-btn--clone" onClick={() => handleClone(s)} title="Clonar como nueva app">
                          ⎘
                        </button>
                        {!s.is_stable && (
                          <button className="snp-btn snp-btn--del" onClick={() => handleDelete(s)} title="Eliminar versión">×</button>
                        )}
                        {s.is_stable && (
                          <button className="snp-btn snp-btn--del snp-btn--del-stable" onClick={() => handleDelete(s)} title="Eliminar versión estable (requiere confirmación)">🔒</button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── B6-2: BuildPlanCard — Plan visual de construcción antes de confirmar ──────
function BuildPlanCard({ card, onConfirm }) {
  const [confirmed, setConfirmed] = useState(false);
  const [adjusted, setAdjusted]   = useState(false);

  const strategyMeta = {
    template: { label: "📦 Template pre-validado", color: "#059669", time: card.estimated_seconds || 5 },
    hybrid:   { label: "⚡ Template + IA",          color: "#0ea5e9", time: card.estimated_seconds || 30 },
    generate: { label: "✨ IA desde cero",           color: "#8b5cf6", time: card.estimated_seconds || 120 },
  };
  const meta    = strategyMeta[card.strategy] || strategyMeta.generate;
  const accent  = card.brand_color || "#5B8DEF";
  const estTime = meta.time < 60 ? `~${meta.time}s` : `~${Math.round(meta.time / 60)} min`;

  const handleConfirm = () => {
    setConfirmed(true);
    if (onConfirm) onConfirm("sí, construye");
  };
  const handleAdjust = () => {
    setAdjusted(true);
    if (onConfirm) onConfirm("quiero ajustar el plan");
  };

  return (
    <div className="bpc-card" style={{ "--bpc-accent": accent }}>
      {/* Header */}
      <div className="bpc-header">
        <div className="bpc-title-row">
          <span className="bpc-app-icon">🏗️</span>
          <div>
            <div className="bpc-app-name">{card.app_name || "Nueva App"}</div>
            {card.app_description && (
              <div className="bpc-app-desc">{card.app_description}</div>
            )}
          </div>
        </div>
        <span className="bpc-strategy-badge" style={{ background: meta.color }}>
          {meta.label}
        </span>
      </div>

      {/* Stack & Template */}
      <div className="bpc-meta-row">
        <span className="bpc-meta-item">⚙️ {card.stack || "FastAPI + MongoDB + React"}</span>
        <span className="bpc-meta-item">⏱ {estTime}</span>
        {card.requires_docker && (
          <span className="bpc-meta-item bpc-docker-badge">🐳 Docker</span>
        )}
      </div>
      {card.template_name && (
        <div className="bpc-template-row">
          <span className="bpc-template-label">Base:</span>
          <span className="bpc-template-name">{card.template_name}</span>
          {card.badge && <span className="bpc-tpl-badge">{card.badge}</span>}
        </div>
      )}

      {/* Screens */}
      {card.screens?.length > 0 && (
        <div className="bpc-section">
          <div className="bpc-section-title">Pantallas</div>
          <div className="bpc-chips">
            {card.screens.map((s, i) => (
              <span key={i} className="bpc-chip">{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      {card.features?.length > 0 && (
        <div className="bpc-section">
          <div className="bpc-section-title">Features</div>
          <div className="bpc-chips">
            {card.features.slice(0, 8).map((f, i) => (
              <span key={i} className="bpc-chip bpc-chip--feature">
                {f.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {!confirmed && !adjusted && (
        <div className="bpc-actions">
          <button className="bpc-btn bpc-btn--confirm" onClick={handleConfirm}>
            ✓ Construir ahora
          </button>
          <button className="bpc-btn bpc-btn--adjust" onClick={handleAdjust}>
            ✎ Ajustar
          </button>
        </div>
      )}
      {confirmed && (
        <div className="bpc-status bpc-status--confirmed">
          ✓ Confirmado — construcción iniciada
        </div>
      )}
      {adjusted && (
        <div className="bpc-status bpc-status--adjusted">
          ✎ Ajuste solicitado
        </div>
      )}
    </div>
  );
}


function VideoJobCard({ card, agent, backendBase }) {
  const [job, setJob] = useState({
    status: card.status || "queued",
    video_url: null,
    error: null,
    duration: card.duration,
  });
  const accent = agent?.color || "#f59e0b";
  const startedRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  // Polling cada 6s mientras esta queued/generating
  useEffect(() => {
    if (job.status === "ready" || job.status === "error") return;
    const tick = async () => {
      try {
        const r = await api.get(`/console/video-jobs/${card.job_id}`);
        setJob(r.data);
      } catch (_) {}
    };
    tick();
    const i = setInterval(tick, 6000);
    return () => clearInterval(i);
  }, [card.job_id, job.status]);

  // Cronometro mientras se genera
  useEffect(() => {
    if (job.status === "ready" || job.status === "error") return;
    const i = setInterval(() => setElapsed(Math.floor((Date.now() - startedRef.current) / 1000)), 1000);
    return () => clearInterval(i);
  }, [job.status]);

  const absUrl = (u) => {
    if (!u) return u;
    if (u.startsWith("http")) return u;
    return (backendBase || "") + u;
  };

  const isReady = job.status === "ready" && job.video_url;
  const isError = job.status === "error";
  const isWorking = !isReady && !isError;

  const eta = card.estimated_wait_sec || (card.duration === 4 ? 180 : (card.duration === 8 ? 300 : 480));
  const progress = isWorking ? Math.min(95, Math.round((elapsed / eta) * 100)) : (isReady ? 100 : 0);

  return (
    <div className="rich-card video-job-card" data-testid="video-job-card" style={{ borderColor: accent }}>
      <div className="rc-head" style={{ background: `linear-gradient(135deg, ${accent}22, transparent)` }}>
        <div className="rc-brand">
          <div className="rc-logo" style={{ background: accent }}>🎥</div>
          <div>
            <div className="rc-brand-name">Sora 2 · Video {card.duration}s</div>
            <div className="rc-brand-sub">
              {card.model || "sora-2"} · {card.size || ""} · {card.aspect}
            </div>
          </div>
        </div>
      </div>
      <div className="vj-body">
        {card.prompt && (
          <div className="vj-prompt">
            <span className="vj-label">PROMPT</span>
            <div>{card.prompt}</div>
          </div>
        )}

        {isWorking && (
          <div className="vj-progress-wrap" data-testid="vj-progress">
            <div className="vj-status">
              <span className="vj-spinner" style={{ borderTopColor: accent }} />
              <span>
                {job.status === "queued" ? "En cola..." : "Generando con Sora 2..."}
                <strong style={{ marginLeft: 8 }}>{elapsed}s</strong>
                <span style={{ color: "var(--text-muted)" }}> / ~{eta}s</span>
              </span>
            </div>
            <div className="vj-progress">
              <div className="vj-progress-fill"
                   style={{ width: `${progress}%`, background: accent }} />
            </div>
            <div className="vj-hint">
              Podés cerrar el chat, el video sigue generándose. Te notificamos al volver.
              <br/><span style={{ color: "#059669" }}>✓ Si Sora 2 falla, los oros se devuelven automáticamente.</span>
            </div>
          </div>
        )}

        {isReady && (
          <div className="vj-video-wrap" data-testid="vj-video-wrap">
            <video
              controls
              playsInline
              className="vj-video"
              data-testid="vj-video"
              src={absUrl(job.video_url)}
              style={{ aspectRatio: card.aspect === "horizontal" ? "16/9" : (card.aspect === "square" ? "1/1" : "9/16") }}
            />
            <div className="vj-actions">
              <a
                href={absUrl(job.video_url)}
                download={`lluvia-sora2-${card.job_id}.mp4`}
                className="vj-download"
                style={{ background: accent }}
                data-testid="vj-download"
              >
                ⬇ Descargar MP4
              </a>
            </div>
          </div>
        )}

        {isError && (
          <div className="vj-error" data-testid="vj-error">
            ✕ {job.error || "La generación falló. Reintentá con otro prompt."}
            {job.refunded && (
              <div style={{ marginTop: "0.5rem", color: "#059669", fontWeight: 600 }}>
                💸 Te reembolsamos los oros automáticamente. Saldo restaurado.
              </div>
            )}
            {!job.refunded && (
              <div style={{ marginTop: "0.5rem", fontSize: "0.78rem", opacity: 0.85 }}>
                Si el cobro no se devolvió, avisanos para revisarlo manualmente.
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
